import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useLiveQuery } from 'dexie-react-hooks';
import { ArrowLeft, ArrowUp, Check, ChevronDown, Grid2x2, Paperclip, Plus, X } from 'lucide-react';

import { canStopAgentChat, stopAgentChat } from '../../lib/agentChatStop';
import { isStudioSidebarWidgetChat, workspaceWidgetChats } from '../../lib/agent_chats';
import { apiGet, apiPost, apiPostAsUser, responseErrorMessage } from '../../lib/api_v2';
import db, { DEXIE_IS_LOADING, clearFingerprint } from '../../lib/db';
import { startDesktopOauth } from '../../lib/desktopOauth';
import { logError, logWarn } from '../../lib/logger';
import {
  MediaAttachmentOwnerTypes,
  MediaAttachmentStates,
  enqueueUpload,
  getMediaAttachmentsForSession,
  isComposerOwnedMediaAttachment,
  removeMediaAttachment,
  removeMediaAttachmentsForSessionKey,
  sortByTimestamp,
  useAllMediaAttachments,
  waitForMediaAttachmentUpload,
} from '../../lib/media_attachments_store';
import { getMetaContent } from '../../lib/meta';
import { exceedsRichdownContentSize } from '../../lib/richdownContentSize';
import { cn, sentenceCase } from '../../lib/utils';
import { promoteWidgetGenerationRun } from '../../lib/widgetGenerationPromotion';
import {
  readyWidgetPreviewVersion,
  timestampValue,
  widgetStudioGenerationPreview,
  widgetVersionLabel,
} from '../../lib/widgetGenerationRun';
import {
  applyWidgetGenerationState,
  clearPendingWidgetGenerationTurn,
  ensureWidgetGenerationTurnReconciler,
  fetchAgentChatWithGenerationTurn,
  storeAgentChatWithGenerationTurn,
  widgetGenerationTurnState,
} from '../../lib/widgetGenerationTurns';
import { WIDGET_PUBLICATION_ACTION, widgetPublicationAction } from '../../lib/widgetPublication';
import {
  WIDGET_REMOVAL_ACTION,
  isInstalledTemplateWidget,
  isUserCreatedWidget,
  removeWidget,
  widgetRemovalAction,
} from '../../lib/widgetRemoval';
import { createWidgetSnapshotCoordinator } from '../../lib/widgetSnapshotCoordinator';

import ExternalLinkIcon from '../../../assets/images/external-link.svg';
import SettingsXIcon from '../../../assets/images/settings-x.svg';
import SpinnerIcon from '../../../assets/images/spinner.svg';
import WidgetAppCreateIcon from '../../../assets/images/widget-app-create.png';
import WidgetCustomImage from '../../../assets/images/widget-custom.png';

import { KEYCHAIN } from '../../contexts/KeyMaster';
import { useRouteWorkspace } from '../../contexts/RouteWorkspaceContext';
import useComposerDropzone from '../../hooks/useComposerDropzone';
import useDesktopWindowFocusSignal from '../../hooks/useDesktopWindowFocusSignal';
import { useHorizontalResize } from '../../hooks/useHorizontalResize';
import useKeyedLiveQuery from '../../hooks/useKeyedLiveQuery';
import { usePendingWidgetGenerationTurnsByChatId } from '../../hooks/useWidgetGenerationBanners';
import { isSingleInstallTemplateInstalled, useWidgetTemplateActions } from '../../hooks/useWidgetTemplateActions';
import useWorkspaceTemplateWidgetSignal from '../../hooks/useWorkspaceTemplateWidgetSignal';
import AgentChatView from '../AgentChatView';
import MediaAttachmentUploadPreview from '../MediaAttachmentUploadPreview';
import { ResizeHandle } from '../ResizeHandle';
import DropOverlay from '../messages/DropOverlay';
import { Flexicon } from '../shared/Flexicon';
import IntegrationStatusBadge from '../shared/IntegrationStatusBadge';
import WidgetAttentionIndicator from '../shared/WidgetAttentionIndicator';
import WidgetEmbed from '../shared/WidgetEmbed';
import WidgetIcon, { bundledTemplateIconUrl, templateIconUrl } from '../shared/WidgetIcon';
import { Modal } from './Modal';
import { ModalBody } from './ModalBody';
import { ModalContent } from './ModalContent';
import { ModalPortal, useModalContext } from './ModalPortal';
import RemoveWidgetConfirmation from './RemoveWidgetConfirmation';

const EMPTY_TEMPLATES = Object.freeze([]);
const EMPTY_MEDIA_ATTACHMENTS = Object.freeze([]);
const EMPTY_INTEGRATIONS = Object.freeze({});
const EMPTY_CUSTOM_WIDGETS = Object.freeze([]);
const EMPTY_WIDGET_CHATS = Object.freeze([]);
// A board tile is 328px wide (w-82). 24px of breathing room each side is the floor
// before the preview starts crowding its own chrome, so that is the column's minimum;
// the chat gets the same floor and starts there, handing the rest to the preview.
// The fallback only covers the frame before the split has been measured.
const WIDGET_PREVIEW_MIN_WIDTH = 376;
const WIDGET_PREVIEW_FALLBACK_WIDTH = 480;
const WIDGET_CHAT_MIN_WIDTH = 376;
const CREATE_WIDGET_ITEM_ID = 'custom:create_widget';
const BROWSE_WIDGETS_ITEM_ID = 'custom:browse_widgets';
const CUSTOM_WIDGET_ITEMS = Object.freeze([
  Object.freeze({
    id: CREATE_WIDGET_ITEM_ID,
    label: 'Create Widget',
    iconUrl: WidgetAppCreateIcon,
    preserveIconColor: true,
    sectionId: 'custom',
    kind: 'create',
  }),
]);

const WIDGET_SIDEBAR_SECTION_LABELS = Object.freeze({
  custom: 'Custom',
  general: 'General',
});
const WIDGET_SIDEBAR_SECTION_ORDER = Object.freeze(['general', 'custom']);

const formatElapsed = (seconds) => {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
};

export function templateDisplayName(template, fallback = 'Widget') {
  return template?.listing?.display_name || template?.name || fallback;
}

export function templateBlurb(template) {
  return template?.listing?.blurb || template?.description || '';
}

function firstTemplateImage(template) {
  return template?.listing?.images?.find((image) => image?.url || image?.thumbnail_url) || null;
}

function customWidgetItemId(widgetId) {
  return `custom:widget:${widgetId}`;
}

function customWidgetIdFromItemId(itemId) {
  return String(itemId || '').startsWith('custom:widget:') ? String(itemId).replace('custom:widget:', '') : null;
}

export function customWidgetChatItemId(agentChatId) {
  return `custom:chat:${agentChatId}`;
}

function customWidgetChatIdFromItemId(itemId) {
  return String(itemId || '').startsWith('custom:chat:') ? String(itemId).replace('custom:chat:', '') : null;
}

function widgetDisplayName(widget, fallback = 'Untitled') {
  return sentenceCase(widget?.title || widget?.default_title || fallback);
}

function widgetCacheKey(widget) {
  return `${widget?.workspace_id || ''}:${widget?.id || ''}`;
}

// Widgets and widget chats share one sidebar list, so their timestamps have to be
// comparable: timestampValue normalizes both ISO strings and second/millisecond epochs.
function sortableSidebarTime(resource) {
  return timestampValue(resource?.updated_at || resource?.created_at || resource?.timestamp || 0);
}

function isCustomWidgetListItem(widget) {
  return Boolean(widget?.id && isUserCreatedWidget(widget) && (widget.status === 'draft' || widget.status === 'published'));
}

function isOwnedCustomWidgetListItem(widget, currentProfileHashid) {
  return Boolean(
    isCustomWidgetListItem(widget) &&
      widget.profile_id &&
      currentProfileHashid &&
      String(widget.profile_id) === String(currentProfileHashid),
  );
}

function isWorkspacePublishedWidget(widget) {
  return Boolean(widget?.id && widget.status === 'published' && widget.official === false);
}

export function isStudioWidgetListItem(widget) {
  return isCustomWidgetListItem(widget) || isWorkspacePublishedWidget(widget);
}

export function workspacePublishedWidgetItemIdForSelection(widgets, selectedItemId) {
  const selectedWidgetId = customWidgetIdFromItemId(selectedItemId);
  const selectedWidgetChatId = customWidgetChatIdFromItemId(selectedItemId);
  const selectedWidget = widgets.find(
    (widget) =>
      isWorkspacePublishedWidget(widget) &&
      ((selectedWidgetId && String(widget.id) === selectedWidgetId) ||
        (selectedWidgetChatId && String(widget.agent_chat_id) === selectedWidgetChatId)),
  );

  return selectedWidget ? customWidgetItemId(selectedWidget.id) : null;
}

export function canHealMissingWidgetChat(widget, currentProfileHashid, linkedWidgetId = null) {
  if (!widget) return !linkedWidgetId;

  return Boolean(
    isUserCreatedWidget(widget) ||
      (widget.profile_id && currentProfileHashid && String(widget.profile_id) === String(currentProfileHashid)),
  );
}

export function isTrackedWidgetReadyForMissingChatRepair(trackedWidgetLoaded, selectedWidgetChatId, activeAgentChatId) {
  return Boolean(trackedWidgetLoaded && (!selectedWidgetChatId || String(activeAgentChatId) === String(selectedWidgetChatId)));
}

const WIDGET_CHAT_SIDEBAR_LIMIT = 20;

// Newest first. Uncapped: every widget row reads its live status off this list, and
// sort_order is a creation ULID, so an older widget rebuilding right now would drop
// out of any window taken here. The cap belongs on the chat rows themselves.
export function studioSidebarWidgetChats(agentChats) {
  return agentChats
    .filter(isStudioSidebarWidgetChat)
    .sort((chatA, chatB) => String(chatB.sort_order || '').localeCompare(String(chatA.sort_order || '')));
}

// Dots and banners read the same generation state. Banner dismissal is a separate,
// explicit action and never changes the state shown in the widget list.
function widgetChatAttentionState(agentChat, pendingTurnsByChatId) {
  if (!agentChat) return null;

  return widgetGenerationTurnState(agentChat, pendingTurnsByChatId?.get(String(agentChat.id)) || null);
}

function widgetSidebarItemForWidget(widget, agentChat, pendingTurnsByChatId) {
  return {
    id: widget.agent_chat_id ? customWidgetChatItemId(widget.agent_chat_id) : customWidgetItemId(widget.id),
    label: widgetDisplayName(widget),
    // Widget JSON icons are full-color images, so they opt out of
    // the active-state invert used for mono glyphs.
    iconUrl: widget.icon_url || null,
    preserveIconColor: true,
    sectionId: 'custom',
    kind: 'custom_widget',
    widgetId: widget.id,
    agentChatId: widget.agent_chat_id || null,
    attentionState: widgetChatAttentionState(agentChat, pendingTurnsByChatId),
    sortAt: sortableSidebarTime(widget),
    widget,
  };
}

function widgetSidebarItemForCustomWidget(widget, agentChat, pendingTurnsByChatId) {
  return isCustomWidgetListItem(widget) ? widgetSidebarItemForWidget(widget, agentChat, pendingTurnsByChatId) : null;
}

export function workspacePublishedWidgetSidebarItems(widgets, widgetChats = EMPTY_WIDGET_CHATS, pendingTurnsByChatId = null) {
  const widgetChatsById = new Map(widgetChats.map((agentChat) => [String(agentChat.id), agentChat]));

  return widgets
    .filter(isWorkspacePublishedWidget)
    .map((widget) => ({
      ...widgetSidebarItemForWidget(widget, widgetChatsById.get(String(widget.agent_chat_id)), pendingTurnsByChatId),
      id: customWidgetItemId(widget.id),
      sectionId: 'workspace',
    }))
    .sort((itemA, itemB) => itemB.sortAt - itemA.sortAt);
}

function widgetSidebarItemForWidgetChat(agentChat, pendingTurnsByChatId) {
  if (!isStudioSidebarWidgetChat(agentChat)) return null;

  return {
    id: customWidgetChatItemId(agentChat.id),
    label: sentenceCase(agentChat.title || 'New widget'),
    iconUrl: WidgetCustomImage,
    preserveIconColor: true,
    sectionId: 'custom',
    kind: 'widget_chat',
    agentChatId: agentChat.id,
    attentionState: widgetChatAttentionState(agentChat, pendingTurnsByChatId),
    sortAt: sortableSidebarTime(agentChat),
    agentChat,
  };
}

function widgetSidebarItemForTemplate(template) {
  if (!template?.slug) return null;

  return {
    id: template.slug,
    label: templateDisplayName(template),
    iconUrl: templateIconUrl(template),
    // Bundled app icons are full-color; server listing icons are mono glyphs
    // that still rely on the active-state invert.
    preserveIconColor: Boolean(bundledTemplateIconUrl(template)),
    sectionId: 'general',
    template,
  };
}

export function widgetSidebarSections(templates, customWidgets, widgetChats, { includeCustom = true, pendingTurnsByChatId = null } = {}) {
  const sectionsById = new Map(
    WIDGET_SIDEBAR_SECTION_ORDER.map((sectionId) => [
      sectionId,
      {
        id: sectionId,
        label: WIDGET_SIDEBAR_SECTION_LABELS[sectionId],
        items: sectionId === 'custom' && includeCustom ? [...CUSTOM_WIDGET_ITEMS] : [],
      },
    ]),
  );

  if (includeCustom) {
    const widgetChatsById = new Map(widgetChats.map((agentChat) => [String(agentChat.id), agentChat]));
    const widgetAgentChatIds = new Set(customWidgets.map((widget) => String(widget.agent_chat_id || '')).filter(Boolean));
    const customItems = [
      ...customWidgets.map((widget) =>
        widgetSidebarItemForCustomWidget(widget, widgetChatsById.get(String(widget.agent_chat_id)), pendingTurnsByChatId),
      ),
      ...widgetChats
        .filter((agentChat) => !widgetAgentChatIds.has(String(agentChat.id)))
        .slice(0, WIDGET_CHAT_SIDEBAR_LIMIT)
        .map((agentChat) => widgetSidebarItemForWidgetChat(agentChat, pendingTurnsByChatId)),
    ]
      .filter(Boolean)
      .sort((itemA, itemB) => itemB.sortAt - itemA.sortAt);

    sectionsById.get('custom').items.push(...customItems);
  }

  templates.forEach((template) => {
    const item = widgetSidebarItemForTemplate(template);
    if (!item) return;

    sectionsById.get(item.sectionId).items.push(item);
  });

  return WIDGET_SIDEBAR_SECTION_ORDER.map((sectionId) => sectionsById.get(sectionId)).filter((section) => section.items.length > 0);
}

function selectedWidgetSidebarItem(sections, selectedItemId) {
  for (const section of sections) {
    const item = section.items.find((candidate) => candidate.id === selectedItemId);
    if (item) return item;
  }

  return null;
}

const WidgetModalCloseButton = React.memo(function WidgetModalCloseButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Close widgets"
      className="relative flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full border border-white bg-gray-100 text-black shadow-[0_4px_4px_0_rgba(0,0,0,0.02)] hover:bg-gray-200"
    >
      <SettingsXIcon className="pointer-events-none size-6" />
    </button>
  );
});

const WidgetModalHeader = React.memo(function WidgetModalHeader({ onClose }) {
  return (
    <div className="flex shrink-0 items-center justify-between gap-4 border-b-[0.5px] border-gray-200 bg-white px-6 py-3">
      <h1 className="min-w-0 truncate font-sans text-[15px]/[28px] font-medium text-black">Widgets</h1>
      <WidgetModalCloseButton onClick={onClose} />
    </div>
  );
});

// `quiet` is the studio rail's register: solid black belongs to its Create new button
// alone, so a selected row there has to read as chosen without borrowing that weight.
const WidgetSidebarItem = React.memo(function WidgetSidebarItem({ item, active, quiet = false, onSelect }) {
  const Icon = item.icon || null;
  const handleSelect = useCallback(() => onSelect(item.id), [item.id, onSelect]);

  return (
    <button
      type="button"
      onClick={handleSelect}
      className={cn(
        // border-white lives in the base, not the active state: the global
        // transition-colors would otherwise animate the border from its currentColor
        // default the instant the 0.5px width appears — a dark stroke flash on click.
        // Toggling only the width keeps the color white from the first frame.
        'flex h-10 w-full shrink-0 cursor-pointer items-center rounded-lg border-white text-left font-sans',
        // The studio rail seats every row on the same 28px icon column so the labels line
        // up whether the row carries a widget's artwork or a lucide glyph.
        quiet ? 'gap-2 px-2 text-[13px]/[18px]' : 'gap-3 px-3 text-[13px]/[28px]',
        quiet
          ? active
            ? 'border-[0.5px] bg-gray-50 font-medium text-black'
            : 'font-normal text-black hover:bg-gray-50/60'
          : active
            ? 'bg-black font-normal text-white'
            : 'font-normal text-black hover:bg-gray-100',
      )}
    >
      {item.widget || item.iconUrl ? (
        <span className={cn('inline-flex shrink-0 items-center justify-center', quiet ? 'size-5' : 'size-9')}>
          {item.widget ? (
            <WidgetIcon widget={item.widget} className={quiet ? 'size-4' : 'size-9'} />
          ) : (
            <img
              src={item.iconUrl}
              alt=""
              className={cn(quiet ? 'size-4' : 'size-9', active && !quiet && !item.preserveIconColor && 'invert')}
              draggable="false"
            />
          )}
        </span>
      ) : Icon ? (
        <span className="inline-flex size-4 shrink-0 items-center justify-center">
          <Icon className="size-4" />
        </span>
      ) : null}
      <span className="min-w-0 flex-1 truncate">{item.label}</span>
      {item.attentionState ? <WidgetAttentionIndicator state={item.attentionState} /> : null}
    </button>
  );
});

const WidgetSidebarSection = React.memo(function WidgetSidebarSection({ section, expanded, selectedItemId, onToggle, onSelect }) {
  const handleToggle = useCallback(() => onToggle(section.id), [onToggle, section.id]);
  const renderItem = useCallback(
    function renderWidgetSidebarItem(item) {
      return <WidgetSidebarItem key={item.id} item={item} active={selectedItemId === item.id} onSelect={onSelect} />;
    },
    [onSelect, selectedItemId],
  );

  return (
    <section className="flex flex-col gap-4">
      <button
        type="button"
        onClick={handleToggle}
        aria-expanded={expanded}
        className="flex h-3 w-full shrink-0 cursor-pointer items-center justify-center gap-2.5 px-2 text-left"
      >
        <span className="min-w-0 flex-1 truncate font-sans text-[12px]/[28px] font-semibold text-gray-400">{section.label}</span>
        <ChevronDown
          size={14}
          strokeWidth={2}
          absoluteStrokeWidth
          className={cn('shrink-0 text-gray-400 transition-transform', !expanded && 'rotate-180')}
        />
      </button>
      {expanded ? <div className="flex flex-col gap-1">{section.items.map(renderItem)}</div> : null}
    </section>
  );
});

const WidgetModalSidebar = React.memo(function WidgetModalSidebar({
  sections,
  selectedItemId,
  expandedSections,
  error,
  loading = false,
  onToggleSection,
  onSelectItem,
  className,
}) {
  const renderSection = useCallback(
    function renderWidgetSidebarSection(section) {
      return (
        <WidgetSidebarSection
          key={section.id}
          section={section}
          expanded={Boolean(expandedSections[section.id])}
          selectedItemId={selectedItemId}
          onToggle={onToggleSection}
          onSelect={onSelectItem}
        />
      );
    },
    [expandedSections, onSelectItem, onToggleSection, selectedItemId],
  );

  return (
    <aside
      className={cn(
        'flex min-h-0 w-58 shrink-0 scrollbar-thin flex-col gap-5 overflow-y-auto border-r-[0.5px] border-gray-200 bg-white px-4 py-6',
        className,
      )}
    >
      {/* The rail's chrome paints with the surface, but its list arrives a beat later.
          Fading the contents in keeps them from snapping into an empty rail partway
          through the presentation. */}
      <div
        className={cn(
          'flex min-h-0 flex-col gap-5 transition-opacity duration-200 ease-settle motion-reduce:transition-none',
          loading ? 'opacity-0' : 'opacity-100',
        )}
      >
        {!loading && sections.length === 0 ? (
          <p className="px-2 py-2 font-sans text-[13px]/[18px] text-gray-400">No widgets available.</p>
        ) : null}
        {sections.map(renderSection)}
        {error ? <p className="px-2 pt-1 font-sans text-[12px]/[16px] text-red-600">{error}</p> : null}
      </div>
    </aside>
  );
});

// The studio's rail, distinct from the library modal's: one prominent action, one link
// into the catalog, and everything you have made underneath. The catalog itself moved
// into a grid — a flat list of every premade widget made the rail long and made your own
// work the thing you had to scroll past.
//
// Both nav rows carry the exact geometry a recent row does — same height, same 28px icon
// column, same gap — so the whole rail reads as one list rather than a button stacked on
// top of one.
const WidgetRailNavRow = React.memo(function WidgetRailNavRow({ icon: Icon, label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        // border-white in the base for the same reason as WidgetSidebarItem: only the
        // width toggles with selection, so the global color transition never animates
        // the border in from currentColor as a dark flash.
        'flex h-10 w-full shrink-0 cursor-pointer items-center gap-2 rounded-lg border-white px-2 text-left font-sans text-[13px]/[18px]',
        active ? 'border-[0.5px] bg-gray-50 font-medium text-black' : 'font-normal text-black hover:bg-gray-50/60',
      )}
    >
      <span className="inline-flex size-5 shrink-0 items-center justify-center">
        <Icon size={12} strokeWidth={1} absoluteStrokeWidth />
      </span>
      <span className="min-w-0 flex-1 truncate">{label}</span>
    </button>
  );
});

// One bar across the whole studio instead of a header per pane. The identity block holds
// the rail's width so the two columns line up beneath it, and back/close live here once
// rather than being re-implemented by every pane that needs an exit.
// The editor's nameplate: the house pill (gray-50 fill, white hairline) with a version
// tag, so it reads as the product's title rather than a nav label. Rendered twice by the
// header — in the rail-width identity block on web, and on native macOS (where the
// traffic lights own that corner) over the content column's blank title slot instead.
const StudioNameplate = ({ className }) => (
  <span
    className={cn(
      'inline-flex min-w-0 items-center gap-1.5 rounded-full border-[0.5px] border-white bg-gray-50 py-1 pr-2.5 pl-3 shadow-[0_1px_2px_rgba(0,0,0,0.04)]',
      className,
    )}
  >
    <span className="truncate font-sans text-[13px]/[18px] font-semibold text-black">Widget Studio</span>
    <span className="shrink-0 font-sans text-[11px]/[16px] font-medium text-gray-400">v.0.1 Beta</span>
  </span>
);

const WidgetStudioHeader = React.memo(function WidgetStudioHeader({ title, creatorProfile, onBack, onClose }) {
  // No dividers on the chrome: the floating card below is what draws the boundaries.
  // The whole band is the window's drag region in Electron; the buttons opt out with
  // data-app-region="none" so they stay clickable inside it.
  return (
    <div data-app-region="drag" className="flex h-14 shrink-0 items-center">
      <div className="flex h-full w-58 shrink-0 items-center pl-4">
        <StudioNameplate className="native-macos:hidden" />
      </div>
      <div className="flex min-w-0 flex-1 items-center gap-1 pr-3 pl-3">
        {/* Only on the blank create page, where it stands in for the title; any real
            title takes the slot for itself. */}
        {title ? null : <StudioNameplate className="hidden native-macos:inline-flex" />}
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            aria-label="Back to widgets"
            data-app-region="none"
            className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-full text-gray-500 hover:bg-white hover:text-black"
          >
            <ArrowLeft size={14} strokeWidth={1.5} absoluteStrokeWidth />
          </button>
        ) : null}
        <div className="flex min-w-0 flex-1 items-center gap-2 px-1">
          <span className="min-w-0 truncate font-sans text-[13px]/[18px] font-semibold text-black">{title}</span>
          <WidgetCreatorAttribution profile={creatorProfile} />
        </div>
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close widget composer"
            data-app-region="none"
            className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-full text-gray-500 hover:bg-white hover:text-black"
          >
            <X size={14} strokeWidth={1.5} absoluteStrokeWidth />
          </button>
        ) : null}
      </div>
    </div>
  );
});

const WidgetComposerSidebar = React.memo(function WidgetComposerSidebar({
  workspaceTitle,
  workspaceItems,
  recentItems,
  selectedItemId,
  error,
  loading = false,
  onSelectItem,
  className,
}) {
  // Create new goes to the blank prompt page, not straight into a bootstrapped chat —
  // the prompt is the first step, and typing there is what opens the builder.
  const handleCreate = useCallback(() => onSelectItem(null), [onSelectItem]);
  const handleBrowse = useCallback(() => onSelectItem(BROWSE_WIDGETS_ITEM_ID), [onSelectItem]);
  const renderItem = useCallback(
    function renderWidgetItem(item) {
      return <WidgetSidebarItem key={item.id} item={item} active={selectedItemId === item.id} quiet onSelect={onSelectItem} />;
    },
    [onSelectItem, selectedItemId],
  );

  return (
    <aside
      className={cn(
        // Transparent over the panel's gray-200, so the rail reads as the shell and the
        // content pane to its right reads as the elevated surface — same blocking as the
        // app's rail in light mode.
        // No border on the rail's edge: the floating pane's gutter and card edge draw
        // the boundary now.
        'flex min-h-0 w-58 shrink-0 scrollbar-thin flex-col gap-4 overflow-y-auto px-3 pb-3',
        className,
      )}
    >
      <div className="flex shrink-0 flex-col gap-0.5">
        {/* Never renders selected — it's an action, not a place, even while the blank
            prompt page it leads to is the one showing. */}
        <WidgetRailNavRow icon={Plus} label="Create new" active={false} onClick={handleCreate} />
        <WidgetRailNavRow icon={Grid2x2} label="Browse widgets" active={selectedItemId === BROWSE_WIDGETS_ITEM_ID} onClick={handleBrowse} />
      </div>
      {/* The rail's chrome paints with the surface, but its list arrives a beat later.
          Fading the contents in keeps them from snapping into an empty rail partway
          through the presentation. */}
      <div
        className={cn(
          'flex min-h-0 flex-col gap-4 transition-opacity duration-200 ease-settle motion-reduce:transition-none',
          loading ? 'opacity-0' : 'opacity-100',
        )}
      >
        {workspaceTitle && workspaceItems.length > 0 ? (
          <section className="flex flex-col gap-1">
            <span className="truncate px-2 font-sans text-[12px]/[16px] font-semibold text-gray-400">{workspaceTitle}</span>
            <div className="flex flex-col gap-0.5">{workspaceItems.map(renderItem)}</div>
          </section>
        ) : null}
        {recentItems.length > 0 ? (
          <section className="flex flex-col gap-1">
            <span className="px-2 font-sans text-[12px]/[16px] font-semibold text-gray-400">Recent</span>
            <div className="flex flex-col gap-0.5">{recentItems.map(renderItem)}</div>
          </section>
        ) : null}
        {error ? <p className="px-2 pt-1 font-sans text-[12px]/[16px] text-red-600">{error}</p> : null}
      </div>
    </aside>
  );
});

const WidgetTemplatePreviewCard = React.memo(function WidgetTemplatePreviewCard({ template }) {
  const image = firstTemplateImage(template);

  return (
    <div className="w-82 max-w-full">
      {/* Matches the builder tile's spec — white hairline, 0.16 shadow. */}
      <article className="relative aspect-square overflow-hidden rounded-[20px] border-[length:var(--output-border-width)] border-white bg-gray-100 shadow-[0_9px_32px_rgba(0,0,0,0.16)]">
        {image ? <img src={image.url || image.thumbnail_url} alt="" className="size-full object-contain" draggable="false" /> : null}
      </article>
    </div>
  );
});

const WIDGET_PROMPT_SUGGESTIONS = Object.freeze([
  'A dice roller with a big result',
  'A countdown to our launch date',
  'A standup rota for the team',
  'A poll with live results',
]);
// The "Based on your chat" pills are parked until they carry real chat-derived prompts.
const SHOW_PROMPT_SUGGESTIONS = false;

// The prompt field's rotating hints — the subtitle copy, broken into placeholders that
// cycle while the field sits empty.
const WIDGET_PROMPT_PLACEHOLDERS = Object.freeze([
  'Describe the widget you want to build…',
  'Describe a problem you want to solve…',
  'Tell us what’s happening in this chat…',
  'What does this group keep track of?',
]);
const WIDGET_PLACEHOLDER_ROTATE_MS = 4000;
const WIDGET_PLACEHOLDER_FADE_MS = 200;
// Capped independently of the agent chat's own limit: this screen is a single opening
// message, not an ongoing conversation, so it doesn't need the same headroom.
const WIDGET_PROMPT_MAX_ATTACHMENTS = 5;
// The field grows with what you type, between a floor that reads as a place to write a
// paragraph and a ceiling that keeps the composer from swallowing the page. Both are
// whole multiples of the 22px line box (4 lines and 10), so a clamped field never shows
// a half-cut line at its edge.
const WIDGET_PROMPT_MIN_HEIGHT = 88;
const WIDGET_PROMPT_MAX_HEIGHT = 220;
// How much of the catalog shows under the prompt before you scroll: its heading, a whole
// first row of cards, and the start of the next — enough to read as a page you continue
// down rather than as one thing with an edge under it. The floor keeps the prompt centered
// in something sane on a short studio, where taking the strip out of the screen would
// leave the composer nowhere to sit.
const WIDGET_CATALOG_PEEK_HEIGHT = 320;
const WIDGET_PROMPT_BAND_MIN_HEIGHT = 320;

// The studio's home: one page carrying both of the rail's top rows. Opening from the
// pane's New button starts here rather than on a template or a bootstrapped chat. The
// prompt is the whole point of the surface, so it takes the first screen; whatever is
// typed there is handed to the builder as its opening message rather than being retyped.
// The catalog sits directly under it — describing a widget and picking a premade one are
// the same decision, so they are the same page, and the rail's two rows scroll between
// them instead of swapping panes.
const WidgetComposerHome = React.memo(function WidgetComposerHome({
  onStart,
  onSelectItem,
  templates,
  loadingTemplates = false,
  scrollTarget,
  scrollNonce = 0,
  workspaceId,
}) {
  const [prompt, setPrompt] = useState('');
  const promptFieldRef = useRef(null);
  const handleChange = useCallback((event) => setPrompt(event.target.value), []);
  const maxPromptByteSize = useMemo(() => parseInt(getMetaContent('agent_chat_message:max_richdown_content_size')), []);
  const trimmedPrompt = prompt.trim();
  const promptTooLarge = exceedsRichdownContentSize(trimmedPrompt, maxPromptByteSize);
  const handleComposerClick = useCallback((event) => {
    if (event.target.closest('button')) return;
    promptFieldRef.current?.focus();
  }, []);

  // Files attached here ride along with the seeded prompt into the builder's chat once
  // it opens — this screen isn't the widget chat itself, so its uploads live under their
  // own channel/session key rather than one tied to an agent chat id that doesn't exist
  // yet. The id only has to be stable for this one mount; nothing reads it afterward.
  const attachmentIdRef = useRef(null);
  if (!attachmentIdRef.current) attachmentIdRef.current = crypto.randomUUID();
  const attachmentChannelId = `widget-composer-home:${attachmentIdRef.current}`;
  const attachmentSessionKey = attachmentChannelId;
  const [attachmentLimitExceeded, setAttachmentLimitExceeded] = useState(false);

  const attachmentsMap = useAllMediaAttachments({ channel_id: attachmentChannelId });
  const promptAttachments = useMemo(
    () =>
      Array.from(attachmentsMap.values())
        .filter((attachment) => isComposerOwnedMediaAttachment(attachment) && attachment.session_key === attachmentSessionKey)
        .sort(sortByTimestamp('written_at')),
    [attachmentsMap, attachmentSessionKey],
  );
  const remainingAttachmentSlots = Math.max(WIDGET_PROMPT_MAX_ATTACHMENTS - promptAttachments.length, 0);
  const isAtMaxAttachmentCapacity = remainingAttachmentSlots === 0;

  // Clears the limit banner as soon as there's room again, same as the builder's composer.
  const previousAttachmentCountRef = useRef(promptAttachments.length);
  useEffect(() => {
    if (promptAttachments.length < previousAttachmentCountRef.current) setAttachmentLimitExceeded(false);
    previousAttachmentCountRef.current = promptAttachments.length;
  }, [promptAttachments.length]);

  // enqueueUpload does async work (thumbnailing, then the Dexie write) before a row
  // exists to read back — Start clicked in that window would otherwise see nothing to
  // wait on and miss the file entirely. Tracked here so collectAttachmentsForStart can
  // await whatever's still in flight before it looks at what's landed in Dexie.
  const pendingEnqueuesRef = useRef(new Set());
  // Attachment intake closes once a start is underway: collectAttachmentsForStart
  // snapshots pendingEnqueuesRef once, so a file dropped/pasted mid-wait wouldn't be
  // in that snapshot and would land as an orphan (hasStartedRef skips cleanup once
  // handoff begins). Declared here, ahead of enqueueFilesForUpload, so drag/drop and
  // paste — not just the paperclip button — respect it too.
  const [isStartingBuilder, setIsStartingBuilder] = useState(false);

  const enqueueFilesForUpload = useCallback(
    (files) => {
      if (!files?.length || !workspaceId || isStartingBuilder) return;
      if (isAtMaxAttachmentCapacity) {
        setAttachmentLimitExceeded(true);
        return;
      }

      const filesToProcess = files.slice(0, remainingAttachmentSlots);
      if (filesToProcess.length < files.length) setAttachmentLimitExceeded(true);

      filesToProcess.forEach((file) => {
        const enqueuePromise = enqueueUpload({
          file,
          purpose: 'agent_chat_message_attachments',
          workspaceId,
          sessionKey: attachmentSessionKey,
          channelId: attachmentChannelId,
          ownerType: MediaAttachmentOwnerTypes.composer,
          ownerId: attachmentChannelId,
        })
          .catch((error) => logError('WidgetComposerHome', 'Failed to enqueue widget prompt attachment', error))
          .finally(() => pendingEnqueuesRef.current.delete(enqueuePromise));
        pendingEnqueuesRef.current.add(enqueuePromise);
      });
    },
    [attachmentChannelId, attachmentSessionKey, isAtMaxAttachmentCapacity, isStartingBuilder, remainingAttachmentSlots, workspaceId],
  );

  const handleDismissAttachmentLimit = useCallback(() => setAttachmentLimitExceeded(false), []);

  const handleRemoveAttachment = useCallback((mediaAttachment) => {
    removeMediaAttachment(mediaAttachment.id).catch((error) => {
      logError('WidgetComposerHome', 'Failed to remove widget prompt attachment', error);
    });
  }, []);

  const handleDropAccepted = useCallback(
    (acceptedFiles) => {
      enqueueFilesForUpload(acceptedFiles);
      promptFieldRef.current?.focus();
    },
    [enqueueFilesForUpload],
  );

  const handleDropRejected = useCallback((rejections) => {
    (rejections || []).forEach(({ file, errors = [] }) => {
      errors.forEach((error) => {
        logWarn('WidgetComposerHome', 'File rejected by dropzone', {
          fileName: file?.name,
          code: error?.code,
          message: error?.message,
        });
      });
    });
  }, []);

  const { dropzoneHelpers, getRootProps, handleDropzoneStateChange, showDropzoneOverlay } = useComposerDropzone({ isInteractive: true });

  useEffect(() => {
    handleDropzoneStateChange({ disabled: isStartingBuilder, onDrop: handleDropAccepted, onDropRejected: handleDropRejected });
    return () => handleDropzoneStateChange(null);
  }, [handleDropzoneStateChange, handleDropAccepted, handleDropRejected, isStartingBuilder]);

  const handleAttachmentButtonClick = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      dropzoneHelpers?.open?.();
    },
    [dropzoneHelpers],
  );

  const handlePromptPaste = useCallback(
    (event) => {
      const files = Array.from(event.clipboardData?.files || []);
      if (!files.length) return;
      event.preventDefault();
      enqueueFilesForUpload(files);
    },
    [enqueueFilesForUpload],
  );

  // Set right before onStart fires, so the cleanup below can tell "handed off to the
  // builder" apart from "abandoned." A successful start still needs these rows to exist
  // a moment longer — AgentChatView reads them off the seeded attachments' own
  // channel/session to finish cleanup (on success) or recover them (on a failed send).
  const hasStartedRef = useRef(false);
  // attemptStart can be mid-wait on an in-flight upload when the viewer navigates away
  // (a template, Browse widgets) — this lets it notice on the other side of that await
  // and back off instead of firing onStart into a screen that already moved on.
  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // An attach-then-abandon (navigating to a template instead of starting) shouldn't
  // leave orphaned uploads behind, so this cleans up whatever this mount enqueued
  // whenever it goes away — unless it's going away because it just handed off.
  useEffect(() => {
    return () => {
      if (hasStartedRef.current) return;
      removeMediaAttachmentsForSessionKey(attachmentSessionKey, {
        ownerType: MediaAttachmentOwnerTypes.composer,
        ownerId: attachmentChannelId,
      }).catch((error) => {
        logError('WidgetComposerHome', 'Failed to clean up widget prompt attachments', error);
      });
    };
  }, [attachmentChannelId, attachmentSessionKey]);
  // A textarea has no intrinsic height, so the field is measured and set on every
  // keystroke: collapse to `auto` so scrollHeight reports the text alone, then clamp.
  // Layout effect, not effect — it lands before paint, so the field never flashes at
  // the wrong height.
  useLayoutEffect(
    function growPromptFieldWithText() {
      const field = promptFieldRef.current;
      if (!field) return;

      field.style.height = 'auto';
      field.style.height = `${Math.min(Math.max(field.scrollHeight, WIDGET_PROMPT_MIN_HEIGHT), WIDGET_PROMPT_MAX_HEIGHT)}px`;
    },
    [prompt],
  );
  // The hint fades out, swaps text, and fades back in on a fixed cadence — but only
  // while the field is empty; the first keystroke stops the show.
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [placeholderVisible, setPlaceholderVisible] = useState(true);
  const hasPrompt = prompt.length > 0;
  useEffect(
    function rotatePromptPlaceholder() {
      if (hasPrompt) return undefined;
      let fadeTimeoutId = null;
      const intervalId = window.setInterval(() => {
        setPlaceholderVisible(false);
        fadeTimeoutId = window.setTimeout(() => {
          setPlaceholderIndex((index) => (index + 1) % WIDGET_PROMPT_PLACEHOLDERS.length);
          setPlaceholderVisible(true);
        }, WIDGET_PLACEHOLDER_FADE_MS);
      }, WIDGET_PLACEHOLDER_ROTATE_MS);
      return () => {
        window.clearInterval(intervalId);
        if (fadeTimeoutId) window.clearTimeout(fadeTimeoutId);
        setPlaceholderVisible(true);
      };
    },
    [hasPrompt],
  );

  // Attachments are read out of the upload store rather than off local state: they're
  // tracked in Dexie by channel/session key (see above), so this is the one-shot read
  // for handoff — same pattern the real composer uses on send. A file dropped or picked
  // right before Start is still uploading (or still being enqueued — see
  // pendingEnqueuesRef) at read time, so both are given a moment to finish first,
  // otherwise they'd silently miss the seeded message. One that ends in error is
  // deleted rather than left behind: it would never show as completed, so nothing
  // would ever collect or clean it up once this mount is gone.
  const collectAttachmentsForStart = useCallback(async () => {
    if (pendingEnqueuesRef.current.size) {
      await Promise.all(Array.from(pendingEnqueuesRef.current));
    }

    // Re-read from Dexie rather than the promptAttachments closure — the enqueues
    // just awaited above may have landed rows this render doesn't know about yet.
    const allAttachments = await getMediaAttachmentsForSession({
      sessionKey: attachmentSessionKey,
      channelId: attachmentChannelId,
      ownerType: MediaAttachmentOwnerTypes.composer,
      ownerId: attachmentChannelId,
      uploadState: null,
    });
    const pending = allAttachments.filter((attachment) => {
      const state = attachment.upload?.state;
      return state && state !== MediaAttachmentStates.completed && state !== MediaAttachmentStates.error;
    });
    if (pending.length) {
      await Promise.all(
        pending.map((attachment) =>
          waitForMediaAttachmentUpload(attachment.id).catch((error) => {
            logWarn('WidgetComposerHome', 'Attachment did not finish uploading before start', { id: attachment.id, error });
            return removeMediaAttachment(attachment.id).catch((removeError) => {
              logError('WidgetComposerHome', 'Failed to clean up a failed widget prompt attachment', removeError);
            });
          }),
        ),
      );
    }

    return getMediaAttachmentsForSession({
      sessionKey: attachmentSessionKey,
      channelId: attachmentChannelId,
      ownerType: MediaAttachmentOwnerTypes.composer,
      ownerId: attachmentChannelId,
    });
  }, [attachmentChannelId, attachmentSessionKey]);

  // Shared by the submit button and Enter: text is required even when a file is
  // attached, because AgentChatView's auto-submit only fires for a non-empty prompt —
  // an attachment-only start would open the builder chat with the image seeded nowhere,
  // silently gone. Keep the viewer here to add a word instead.
  const attemptStart = useCallback(async () => {
    if (isStartingBuilder) return;
    if (promptTooLarge) {
      promptFieldRef.current?.focus();
      return;
    }

    setIsStartingBuilder(true);
    let attachments;
    try {
      attachments = await collectAttachmentsForStart();
    } catch (error) {
      // A rejected Dexie read shouldn't leave Start permanently disabled with no way
      // to retry — surface it as a no-op instead.
      if (isMountedRef.current) setIsStartingBuilder(false);
      logError('WidgetComposerHome', 'Failed to collect widget prompt attachments', error);
      return;
    }
    // Waiting on an in-flight upload can outlast this mount: the viewer may have
    // already navigated to a template or Browse widgets, which already ran the
    // abandon-cleanup above. Don't force them back into Create Widget on top of it.
    if (!isMountedRef.current) return;

    if (!trimmedPrompt && attachments.length > 0) {
      // Checked against the resolved attachment set, not the promptAttachments
      // snapshot from before collectAttachmentsForStart's wait — attach-then-Start
      // before that live query catches up would otherwise slip past an earlier
      // check here and hand off an empty prompt that auto-submit never fires for.
      setIsStartingBuilder(false);
      promptFieldRef.current?.focus();
      return;
    }

    hasStartedRef.current = true;
    onStart(trimmedPrompt, attachments);
  }, [collectAttachmentsForStart, isStartingBuilder, onStart, promptTooLarge, trimmedPrompt]);
  const handleSubmit = useCallback(
    function startFromPrompt(event) {
      event.preventDefault();
      attemptStart();
    },
    [attemptStart],
  );
  // Enter sends, shift+Enter breaks the line — the same bargain the builder's composer
  // strikes, so the field behaves the way it looks.
  const handleKeyDown = useCallback(
    function submitOnEnter(event) {
      if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;
      event.preventDefault();
      attemptStart();
    },
    [attemptStart],
  );
  const renderSuggestion = useCallback(
    function renderWidgetPromptSuggestion(suggestion) {
      return (
        <button
          key={suggestion}
          type="button"
          onClick={() => onStart(suggestion)}
          className="cursor-pointer rounded-full border-[0.5px] border-white bg-white px-3.5 py-2 font-sans text-[13px]/[18px] text-gray-700 shadow-[0_1px_2px_rgba(0,0,0,0.04)] hover:text-black hover:shadow-[0_4px_16px_rgba(0,0,0,0.08)]"
        >
          {suggestion}
        </button>
      );
    },
    [onStart],
  );
  // Create new and Browse widgets are two places on this one page rather than two pages,
  // so the rail's rows are anchors: each scrolls its section into view inside this
  // scroller. `scrollNonce` is what drives it rather than the target alone — clicking the
  // row you are already "on" has to scroll back to it, and a target that has not changed
  // would be a no-op.
  const renderTemplateCard = useCallback(
    function renderWidgetCatalogCard(template) {
      const iconUrl = templateIconUrl(template);

      return (
        <button
          key={template.slug}
          type="button"
          onClick={() => onSelectItem(template.slug)}
          className="flex cursor-pointer flex-col gap-3 rounded-lg border-[0.5px] border-gray-200 bg-white px-3 pt-2 pb-3 text-left shadow-[0_1px_2px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_16px_rgba(0,0,0,0.08)]"
        >
          <span className="flex size-7 items-center justify-center">
            {iconUrl ? <img src={iconUrl} alt="" className="size-7 object-contain" draggable="false" /> : null}
          </span>
          <span className="font-sans text-[13px]/[20px] font-semibold text-black">{templateDisplayName(template)}</span>
          <p className="font-sans text-[13px]/[18px] text-gray-500">{templateBlurb(template)}</p>
        </button>
      );
    },
    [onSelectItem],
  );
  const promptSectionRef = useRef(null);
  const gridSectionRef = useRef(null);
  // The prompt centers in the screen minus a strip, so the catalog's heading and the top
  // of its first row show under it while the prompt is still the thing you landed on.
  // Measured, not a percentage: this section's parent is content-height, so a percentage
  // has nothing to resolve against — and the strip has to hold its size as the studio is
  // resized rather than scaling away.
  const scrollerRef = useRef(null);
  const [promptBandHeight, setPromptBandHeight] = useState(null);
  useLayoutEffect(function trackPromptBandHeight() {
    const scroller = scrollerRef.current;
    if (!scroller || typeof ResizeObserver === 'undefined') return undefined;

    const measure = () => setPromptBandHeight(Math.max(WIDGET_PROMPT_BAND_MIN_HEIGHT, scroller.clientHeight - WIDGET_CATALOG_PEEK_HEIGHT));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(scroller);
    return () => observer.disconnect();
  }, []);
  useEffect(
    function scrollToRequestedSection() {
      // Nothing on first paint: the page opens at the prompt on its own, and smooth-
      // scrolling to the top of a scroller already at the top just costs a frame.
      if (!scrollNonce) return;

      const section = scrollTarget === 'grid' ? gridSectionRef.current : promptSectionRef.current;
      section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    [scrollNonce, scrollTarget],
  );

  return (
    <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
      <div ref={scrollerRef} className="flex min-h-0 flex-1 scrollbar-thin flex-col items-center overflow-y-auto px-6">
        {/* The column runs wide for the catalog below; the prompt keeps its own 560px cap
            so the composer stays a focused block in the middle. */}
        <div className="flex w-full max-w-[1100px] flex-col items-center">
          <section
            ref={promptSectionRef}
            style={promptBandHeight ? { minHeight: promptBandHeight } : undefined}
            className="flex w-full scroll-mt-6 flex-col items-center justify-center gap-8 pt-24 pb-16"
          >
            <h2 className="text-center font-sans text-[28px]/[34px] font-semibold tracking-[-0.02em] text-black">
              Make something for your chat
            </h2>
            <form onSubmit={handleSubmit} className="w-full max-w-[560px]">
              {/* White on the studio's gray surface, the house card treatment: the fill is
                the contrast, and the shadow deepens as the field wakes up.

                The send button floats over the field's bottom-right rather than taking a
                row of its own — a whole row spent on a 32px button is the row the writing
                wants. `pr-11` holds the button's column clear for the field's full height,
                so no line ever runs underneath it, while the last line still sits beside
                it instead of being pushed above. */}
              <div
                {...getRootProps({
                  className:
                    'relative w-full cursor-text rounded-2xl border-[0.5px] border-white bg-white p-3 shadow-[0_1px_3px_rgba(0,0,0,0.05)] focus-within:shadow-[0_6px_24px_rgba(0,0,0,0.10)]',
                })}
                onClick={handleComposerClick}
              >
                <DropOverlay isVisible={showDropzoneOverlay} label="Attach to widget prompt" zIndexVariant="widgetComposerHome" />
                <input {...dropzoneHelpers.getInputProps({ multiple: true })} className="hidden" />
                <textarea
                  ref={promptFieldRef}
                  autoFocus
                  value={prompt}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  onPaste={handlePromptPaste}
                  rows={1}
                  aria-label="Describe the widget you want to build"
                  aria-describedby={promptTooLarge ? 'widget-prompt-size-error' : undefined}
                  aria-invalid={promptTooLarge}
                  style={{ minHeight: WIDGET_PROMPT_MIN_HEIGHT, maxHeight: WIDGET_PROMPT_MAX_HEIGHT }}
                  // pb-12 reserves the same band the paperclip/thumbnails/send row sits
                  // in (bottom-3 + their 36px height) so a long prompt's last line wraps
                  // above it instead of running underneath — the row is always there,
                  // attachments or not, since the paperclip itself always renders.
                  className="block w-full resize-none scrollbar-thin bg-transparent pr-11 pb-12 pl-1 font-sans text-[15px]/[22px] text-black outline-none"
                />
                {/* The rotating hint stands in for the native placeholder, which can't
                  animate. aria-hidden — the textarea's aria-label carries the meaning. */}
                {hasPrompt ? null : (
                  <span
                    aria-hidden
                    style={{ transitionDuration: `${WIDGET_PLACEHOLDER_FADE_MS}ms` }}
                    className={cn(
                      // Insets match the field's own text box — 12px card padding plus the
                      // textarea's pl-1 / pr-11 — so the hint sits exactly where the first
                      // character will land.
                      'pointer-events-none absolute top-3 right-14 left-4 font-sans text-[15px]/[22px] text-gray-400 transition-opacity motion-reduce:transition-none',
                      placeholderVisible ? 'opacity-100' : 'opacity-0',
                    )}
                  >
                    {WIDGET_PROMPT_PLACEHOLDERS[placeholderIndex]}
                  </span>
                )}
                <button
                  type="button"
                  onClick={handleAttachmentButtonClick}
                  disabled={isAtMaxAttachmentCapacity || isStartingBuilder}
                  aria-label="Attach a file"
                  className="absolute bottom-3 left-3 flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-black disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Paperclip size={16} strokeWidth={2} />
                </button>
                {/* Thumbnails ride right beside the button that adds them rather than
                    stacking above the text — same row, same 12px gap the buttons keep
                    from the card's edges, scrolling sideways if the cap's five ever
                    don't fit. */}
                {(promptAttachments.length > 0 || attachmentLimitExceeded) && (
                  <div className="absolute right-14 bottom-3 left-14 flex items-center gap-1.5 overflow-x-auto">
                    {promptAttachments.map((attachment) => (
                      <MediaAttachmentUploadPreview
                        key={attachment.id}
                        mediaAttachment={attachment}
                        size="size-9"
                        rounded="rounded-lg"
                        showRemoveButton
                        removeButtonSize="small"
                        onRemove={handleRemoveAttachment}
                      />
                    ))}
                    {attachmentLimitExceeded && (
                      <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-neutral-950/80 py-1 pr-1.5 pl-2.5 font-sans text-2xs font-medium whitespace-nowrap text-white">
                        Only {WIDGET_PROMPT_MAX_ATTACHMENTS} files at a time
                        <button
                          type="button"
                          className="flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-full hover:bg-white/10"
                          onClick={handleDismissAttachmentLimit}
                          aria-label="Dismiss"
                        >
                          &times;
                        </button>
                      </span>
                    )}
                  </div>
                )}
                <button
                  type="submit"
                  aria-label="Start building"
                  disabled={isStartingBuilder || promptTooLarge}
                  className="absolute right-3 bottom-3 flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full bg-black text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isStartingBuilder ? <SpinnerIcon className="size-4 animate-spin" /> : <ArrowUp size={18} strokeWidth={2} />}
                </button>
              </div>
              {promptTooLarge ? (
                <p id="widget-prompt-size-error" role="alert" className="px-3 pt-2 font-sans text-[12px]/[16px] text-red-600">
                  Prompt is too long. Shorten it to continue.
                </p>
              ) : null}
            </form>
            {/* Hidden for now. Capped at the prompt's width so the two read as one
              centered stack. */}
            {SHOW_PROMPT_SUGGESTIONS ? (
              <div className="flex w-full max-w-[560px] flex-col items-center gap-4">
                <span className="font-sans text-[12px]/[16px] font-semibold text-gray-400">Based on your chat</span>
                <div className="flex flex-wrap items-center justify-center gap-2">{WIDGET_PROMPT_SUGGESTIONS.map(renderSuggestion)}</div>
              </div>
            ) : null}
          </section>
          {/* No scroll-margin: the anchor lands the section's own top edge on the
              scroller's, and pt-16 is already the air above the heading. Adding a margin
              on top of the padding counts the same gap twice and stops the scroll short. */}
          <section ref={gridSectionRef} className="w-full pt-12 pb-10">
            {/* Reads as the page's second heading rather than a label over the grid, so it
                carries the prompt heading's treatment a size down — centered, on the same
                axis the prompt above it sits on. */}
            <h2 className="pb-9 text-center font-sans text-[20px]/[26px] font-semibold tracking-[-0.01em] text-black">Browse widgets</h2>
            {loadingTemplates ? (
              <div className="flex items-center justify-center py-10" role="status" aria-label="Loading widgets">
                <SpinnerIcon className="size-5 animate-spin text-gray-500" />
              </div>
            ) : templates.length === 0 ? (
              <p className="font-sans text-[13px]/[18px] text-gray-400">No widgets available.</p>
            ) : (
              <div className="grid auto-rows-fr grid-cols-2 gap-2.5 lg:grid-cols-3">{templates.map(renderTemplateCard)}</div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
});

const WidgetModalLoadingState = React.memo(function WidgetModalLoadingState() {
  return (
    <div className="flex min-h-0 min-w-0 flex-1 items-center justify-center" role="status" aria-label="Loading widgets">
      <SpinnerIcon className="size-5 animate-spin text-gray-500" />
    </div>
  );
});

const WidgetModalErrorState = React.memo(function WidgetModalErrorState({ message, onRetry }) {
  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col items-center justify-center gap-3 p-6 text-center" role="alert">
      <p className="font-sans text-[13px]/[18px] text-red-600">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="cursor-pointer rounded bg-gray-100 px-3 py-1.5 font-sans text-[12px]/[18px] font-medium text-gray-700 hover:bg-gray-200"
      >
        Try again
      </button>
    </div>
  );
});

export const WidgetCreatorAttribution = React.memo(function WidgetCreatorAttribution({ profile }) {
  if (!profile?.full_name) return null;

  return (
    <span className="flex shrink-0 items-center gap-1 font-sans text-[12px]/[18px] font-normal text-gray-400">
      <Flexicon resource={profile} size="size-4" />
      <span>Created by {profile.full_name}</span>
    </span>
  );
});

const WidgetBuilderPanel = React.memo(function WidgetBuilderPanel({
  activeAgentChatId,
  buildElapsedSeconds,
  buildStatusLabel,
  canDelete,
  canManagePublication,
  canManageRemoval,
  canPublish,
  canStop,
  canUnpublish,
  composerAutoSubmit,
  composerAutoSubmitAttachments,
  creatorProfile,
  errors,
  // When set, the panel owns the whole surface (the drawer composer) rather than
  // sitting beside the sidebar, so it renders its own close affordance and pins the
  // preview column to the width a board tile actually gets.
  fillsSurface = false,
  headless = false,
  handleOpenDeleteConfirm,
  handlePublish,
  handleUnpublish,
  hasPreview,
  isBootstrapping,
  isBuildInProgress,
  isDeleting,
  isLegacyPinned,
  isLoadingBuilderWidget,
  isPublishing,
  isRunning,
  isStopping,
  isUnpublishing,
  onClose,
  onStopTargetChange,
  onStop,
  previewHeight,
  previewUrl,
  removalAction,
  title,
  trackedWidget,
  versionLabel,
}) {
  // Measured, so the drag can stop exactly where the chat hits its floor rather than at
  // a guessed ceiling: the studio's width depends on the rail and the widgets pane.
  const splitRef = useRef(null);
  const [splitWidth, setSplitWidth] = useState(0);
  useEffect(
    function trackSplitWidth() {
      const element = splitRef.current;
      if (!element || typeof ResizeObserver === 'undefined') return undefined;

      const observer = new ResizeObserver(([entry]) => setSplitWidth(entry.contentRect.width));
      observer.observe(element);
      return () => observer.disconnect();
    },
    [fillsSurface],
  );
  const previewMaxWidth =
    splitWidth > 0 ? Math.max(WIDGET_PREVIEW_MIN_WIDTH, splitWidth - WIDGET_CHAT_MIN_WIDTH) : WIDGET_PREVIEW_FALLBACK_WIDTH;
  const previewResize = useHorizontalResize({
    initialWidth: WIDGET_PREVIEW_FALLBACK_WIDTH,
    minWidth: WIDGET_PREVIEW_MIN_WIDTH,
    maxWidth: previewMaxWidth,
    direction: 'right',
  });
  const { setWidth: setPreviewWidth } = previewResize;
  // Open with the chat at its floor and the preview holding everything else. The real
  // estate only exists once the split has been measured, so this waits for that first
  // measurement — and seeds once, so a later drag is not fought by the default.
  const didSeedPreviewWidthRef = useRef(false);
  useEffect(
    function openWithChatAtItsFloor() {
      if (didSeedPreviewWidthRef.current || splitWidth <= 0) return;

      didSeedPreviewWidthRef.current = true;
      setPreviewWidth(splitWidth - WIDGET_CHAT_MIN_WIDTH);
    },
    [setPreviewWidth, splitWidth],
  );
  // The hook only clamps while dragging, so a window resize needs clamping here too.
  const previewWidth = Math.min(previewResize.width, previewMaxWidth);
  const actionButtons = (
    <>
      {removalAction === WIDGET_REMOVAL_ACTION.DELETE && canManageRemoval ? (
        <button
          type="button"
          onClick={handleOpenDeleteConfirm}
          disabled={!canDelete}
          className="shrink-0 cursor-pointer rounded px-2.5 py-1.5 font-sans text-[12px]/[18px] font-medium text-gray-500 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isDeleting ? 'Deleting...' : 'Delete'}
        </button>
      ) : null}
      {removalAction === WIDGET_REMOVAL_ACTION.UNPUBLISH && canManagePublication ? (
        <button
          type="button"
          onClick={handleUnpublish}
          disabled={!canUnpublish}
          className={cn(
            'shrink-0 cursor-pointer rounded px-2.5 py-1.5 font-sans text-[12px]/[18px] font-medium',
            canUnpublish ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' : 'bg-gray-100 text-gray-300',
          )}
        >
          {isUnpublishing ? 'Unpublishing...' : 'Unpublish'}
        </button>
      ) : null}
      {canManagePublication && (canPublish || isPublishing) ? (
        <button
          type="button"
          className={cn(
            'shrink-0 cursor-pointer rounded px-2.5 py-1.5 font-sans text-[12px]/[18px] font-medium',
            canPublish ? 'bg-blue-500 text-white hover:bg-blue-600' : 'bg-gray-100 text-gray-300',
          )}
          onClick={handlePublish}
          disabled={!canPublish}
        >
          {isPublishing ? 'Publishing...' : 'Publish'}
        </button>
      ) : null}
    </>
  );

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-white">
      {headless ? null : (
        <div className="shrink-0 border-b-[0.5px] border-gray-200 bg-white px-4 py-3">
          <div className="flex min-h-10 items-center justify-between gap-4">
            <div className="flex min-w-0 flex-1 items-baseline gap-2">
              <h2 className="min-w-0 truncate font-sans text-[16px] font-semibold text-black">{title}</h2>
              <WidgetCreatorAttribution profile={creatorProfile} />
              {versionLabel ? (
                <span className="shrink-0 font-sans text-[12px]/[18px] font-normal text-gray-400">{versionLabel}</span>
              ) : null}
              {isLegacyPinned ? (
                <span className="shrink-0 font-sans text-[12px]/[18px] font-medium text-amber-600">Legacy / pinned</span>
              ) : null}
            </div>

            <div className="flex shrink-0 items-center gap-2">
              {actionButtons}
              {onClose ? (
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close widget composer"
                  className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-black"
                >
                  <X className="size-4.5" strokeWidth={2} absoluteStrokeWidth />
                </button>
              ) : null}
            </div>
          </div>

          {errors.general ? <p className="pt-2 font-sans text-[13px]/[18px] text-red-600">{errors.general}</p> : null}
        </div>
      )}

      <div ref={splitRef} className="flex min-h-0 flex-1 flex-col md:flex-row">
        <div
          style={fillsSurface ? { '--widget-preview-width': `${previewWidth}px` } : undefined}
          className={cn(
            // gray-50 ground, one step under the chat's white: the gray-100 tile (which
            // never changes) sits on its stroke and shadow to hold its edge.
            'relative flex min-h-0 w-full shrink-0 flex-col border-b-[0.5px] border-gray-200/60 bg-gray-50 md:border-r-[0.5px] md:border-b-0',
            // Drafting-dot grid on the stage: a repeated 1px radial gradient — one tiny
            // raster tile, no image fetch, no animation — so it costs nothing while
            // giving the widget a canvas to sit on.
            'bg-[radial-gradient(var(--color-gray-200)_1px,transparent_1px)] [background-size:16px_16px]',
            // A percentage column only adds dead margin on the wide composer surface, so
            // the studio drags a real width instead. Below md the two stack and the
            // width goes back to being the full row.
            fillsSurface ? 'md:w-[var(--widget-preview-width)]' : 'md:w-[45%]',
          )}
        >
          <div className="flex max-h-96 flex-col gap-3 overflow-auto p-4 md:max-h-none md:min-h-0 md:flex-1">
            {/* Parked here while the header is a pure nav bar: these act on the widget the
                preview is showing, so they ride above it rather than beside the title. The
                version reads first on the left; the actions collect on the right, ordered
                least to most destructive-to-undo — same order the header bar uses. */}
            {headless ? (
              <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 px-1">
                <div className="flex min-w-0 items-center gap-2">
                  {versionLabel ? (
                    <span className="shrink-0 font-sans text-[12px]/[18px] font-normal text-gray-400">{versionLabel}</span>
                  ) : null}
                  {isLegacyPinned ? (
                    <span className="shrink-0 font-sans text-[12px]/[18px] font-medium text-amber-600">Legacy / pinned</span>
                  ) : null}
                </div>
                <div className="flex shrink-0 items-center gap-2">{actionButtons}</div>
              </div>
            ) : null}
            <div className="relative my-auto w-82 max-w-full self-center">
              {/* The tile spec, shared with WidgetTemplatePreviewCard: white hairline,
                  deepened shadow — on the gray-50 ground the shadow is what draws the
                  edge. Same border width (--output-border-width), so position holds. */}
              <div className="relative overflow-hidden rounded-[20px] border-[length:var(--output-border-width)] border-white bg-gray-100 shadow-[0_9px_32px_rgba(0,0,0,0.16)]">
                {hasPreview ? (
                  <WidgetEmbed
                    title={title}
                    src={previewUrl}
                    height={previewHeight}
                    className="bg-white"
                    agentChatId={activeAgentChatId || trackedWidget?.agent_chat_id}
                    widgetId={trackedWidget?.id}
                    viewerSessionEnabled={trackedWidget?.viewer_session_enabled}
                  />
                ) : (
                  <div className="flex aspect-square w-full items-center justify-center bg-gray-100">
                    {isRunning ? <SpinnerIcon className="size-5 animate-spin text-gray-400" /> : null}
                  </div>
                )}
                {isBuildInProgress ? (
                  <div className="absolute inset-0 z-(--stack-widget-builder-progress) flex items-center justify-center bg-white/95 p-6 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <SpinnerIcon className="size-5 animate-spin text-gray-500" />
                      <div className="flex flex-col items-center gap-0.5 text-center">
                        <p className="font-sans text-[14px]/[20px] font-medium text-gray-700">{buildStatusLabel}</p>
                        {buildElapsedSeconds != null ? (
                          <p className="font-sans text-[12px]/[18px] text-gray-600 tabular-nums">{formatElapsed(buildElapsedSeconds)}</p>
                        ) : null}
                        {canStop ? (
                          <button
                            type="button"
                            className="cursor-pointer pt-1 font-sans text-[12px]/[18px] font-medium text-gray-600 hover:text-black disabled:cursor-not-allowed disabled:opacity-50"
                            onClick={onStop}
                            disabled={isStopping}
                          >
                            {isStopping ? 'Stopping…' : 'Stop'}
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
            {errors.general && headless ? (
              // Absolutely positioned against the stage column: an in-flow sibling here
              // would push the my-auto-centered tile up when the error appears — the
              // message overlays the stage's bottom edge without moving anything.
              <p className="absolute inset-x-5 bottom-4 font-sans text-[13px]/[18px] text-red-600">{errors.general}</p>
            ) : null}
          </div>
          {fillsSurface ? (
            <ResizeHandle
              position="right"
              isResizing={previewResize.isResizing}
              currentWidth={previewWidth}
              minWidth={WIDGET_PREVIEW_MIN_WIDTH}
              maxWidth={previewMaxWidth}
              className="max-md:hidden"
              {...previewResize.getHandleProps()}
            />
          ) : null}
        </div>

        <div className="min-h-0 min-w-0 flex-1 bg-white md:w-[55%]">
          {activeAgentChatId ? (
            <AgentChatView
              agentChatId={activeAgentChatId}
              composerPlaceholder="Describe the widget you want to build"
              composerAutoSubmit={composerAutoSubmit}
              composerAutoSubmitAttachments={composerAutoSubmitAttachments}
              compact={fillsSurface}
              onStopTargetChange={onStopTargetChange}
              pinComposerToBottom
              showHeader={false}
            />
          ) : (
            <div className="flex h-full items-center justify-center font-sans text-[14px]/[20px] text-gray-500">
              {isBootstrapping || isLoadingBuilderWidget ? (
                <SpinnerIcon className="size-5 animate-spin text-gray-400" />
              ) : (
                <p>Widget chat unavailable.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

const WidgetTemplateDetailPanel = React.memo(function WidgetTemplateDetailPanel({
  item,
  template,
  integrationStatus,
  isAdmin,
  isInstalling,
  isRemoving,
  wasInstalled,
  installError,
  canInstall,
  canRemove,
  workspaceId,
  onInstall,
  onRemove,
  onError,
  desktopFocusSignal,
  // Selection mode (pre-creation Create Group flow): the action button toggles a
  // local "Added" selection instead of installing into a workspace.
  selectionMode = false,
  isSelected = false,
  onToggleSelected = null,
  // The studio's spanning header carries the name and the exit; this panel keeps only
  // the verbs that act on the template.
  headless = false,
  onClose = null,
}) {
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const awaitingDesktopReturnRef = useRef(false);
  const title = template ? templateDisplayName(template, item?.label) : 'Widget';
  const blurb = templateBlurb(template);
  const provider = template?.required_integrations?.[0] || null;
  const configured = provider ? integrationStatus?.configured === true : true;
  const connected = provider ? configured && Boolean(integrationStatus?.connected) : true;
  const isAlreadyInstalled = !selectionMode && isSingleInstallTemplateInstalled(template);
  const actionLabel = selectionMode
    ? isSelected
      ? 'Added'
      : 'Add to chat'
    : isRemoving
      ? 'Removing...'
      : isAlreadyInstalled
        ? 'Remove'
        : isInstalling
          ? 'Adding...'
          : wasInstalled
            ? 'Added'
            : 'Add widget';
  const canUseAction = selectionMode ? Boolean(template) : isAlreadyInstalled ? canRemove : canInstall && connected;
  const handleAction = selectionMode ? onToggleSelected : isAlreadyInstalled ? onRemove : onInstall;
  // "On" = the action button would undo (deselect/remove) rather than add.
  const actionIsOn = selectionMode ? isSelected : isAlreadyInstalled;
  const integrationHint = !provider
    ? null
    : !configured
      ? `${title} is not configured yet.`
      : !connected && !isAdmin
        ? `An admin needs to connect ${title} first.`
        : null;
  const handleAuthorizeClick = useCallback(
    async function handleIntegrationAuthorizeClick() {
      if (!isAdmin || !workspaceId || !provider || isAuthorizing || !configured) return;

      setIsAuthorizing(true);
      onError(null, template);
      let desktopOauth = null;

      try {
        desktopOauth = await startDesktopOauth({ purpose: 'integration', provider, workspaceId });
        if (desktopOauth) {
          awaitingDesktopReturnRef.current = !desktopOauth.completed;
          return;
        }

        const response = await apiPostAsUser(`/workspaces/${workspaceId}/integrations/${provider}/authorize`, {
          client: 'web',
        });
        const data = await response.json();
        const authorizationUrl = data.data?.authorization_url;

        if (response.ok && data.ok === true && authorizationUrl) {
          window.location.href = authorizationUrl;
          return;
        }

        onError(`Unable to open ${title}.`, template);
        logError('WidgetStudio', 'Failed to authorize integration', data.errors);
      } catch (authorizeError) {
        onError(`Unable to open ${title}.`, template);
        logError('WidgetStudio', 'Failed to authorize integration', authorizeError);
      } finally {
        if (!desktopOauth || desktopOauth.completed) setIsAuthorizing(false);
      }
    },
    [configured, isAdmin, isAuthorizing, onError, provider, template, title, workspaceId],
  );

  useEffect(
    function reenableAuthorizeOnDesktopReturn() {
      if (!awaitingDesktopReturnRef.current) return;

      awaitingDesktopReturnRef.current = false;
      setIsAuthorizing(false);
    },
    [desktopFocusSignal],
  );

  if (!item || !template) {
    return <div aria-hidden className="min-h-0 min-w-0 flex-1 bg-white" />;
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-white">
      {headless ? null : (
        <div className="flex shrink-0 items-center justify-between gap-4 border-b-[0.5px] border-gray-200 px-6 py-3">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {headless ? null : <h2 className="min-w-0 truncate font-sans text-[16px] font-semibold text-black">{title}</h2>}
            {provider ? <IntegrationStatusBadge connected={connected} /> : null}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {provider && isAdmin && !selectionMode ? (
              <button
                type="button"
                className="inline-flex h-10 cursor-pointer items-center gap-1.5 rounded-lg px-2 font-sans text-[12px]/[18px] font-semibold text-gray-500 hover:bg-gray-100 hover:text-black disabled:cursor-not-allowed disabled:opacity-50"
                disabled={isAuthorizing || !configured}
                onClick={handleAuthorizeClick}
              >
                <ExternalLinkIcon className="size-3.5 shrink-0" />
                <span>{!configured ? `${title} unavailable` : connected ? `Manage ${title}` : `Connect ${title}`}</span>
              </button>
            ) : null}

            <button
              type="button"
              onClick={handleAction}
              disabled={!canUseAction}
              className={cn(
                'flex h-10 shrink-0 cursor-pointer items-center justify-center rounded-xl px-4 font-sans text-[13px]/[18px] text-white disabled:cursor-not-allowed',
                canUseAction && actionIsOn && (selectionMode ? 'bg-black hover:bg-neutral-800' : 'bg-red-500 hover:bg-red-600'),
                canUseAction && !actionIsOn && 'bg-blue-500 hover:bg-blue-600',
                !canUseAction && 'bg-gray-200 text-gray-400',
              )}
            >
              {isInstalling || isRemoving ? <SpinnerIcon className="mr-2 size-3.5 animate-spin" /> : null}
              {selectionMode && isSelected ? <Check size={18} strokeWidth={1.5} absoluteStrokeWidth className="mr-1.5" /> : null}
              {actionLabel}
            </button>
            {!headless && onClose ? (
              <button
                type="button"
                onClick={onClose}
                aria-label="Close widget composer"
                className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-black"
              >
                <X className="size-4.5" strokeWidth={2} absoluteStrokeWidth />
              </button>
            ) : null}
          </div>
        </div>
      )}

      {/* gray-50 ground, matching the builder's stage: the tile holds its edge on its
          own stroke and shadow rather than on a tone difference. */}
      <div className="flex shrink-0 items-center justify-center border-b-[0.5px] border-gray-200/60 bg-gray-50 px-6 py-15">
        <WidgetTemplatePreviewCard template={template} />
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-6">
        {/* Parked here while the header is a pure nav bar: these act on the template, so
            they sit with it rather than competing with the title and the exit. */}
        {headless ? (
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleAction}
              disabled={!canUseAction}
              className={cn(
                'flex h-10 shrink-0 cursor-pointer items-center justify-center rounded-xl px-4 font-sans text-[13px]/[18px] text-white disabled:cursor-not-allowed',
                canUseAction && actionIsOn && (selectionMode ? 'bg-black hover:bg-neutral-800' : 'bg-red-500 hover:bg-red-600'),
                canUseAction && !actionIsOn && 'bg-blue-500 hover:bg-blue-600',
                !canUseAction && 'bg-gray-200 text-gray-400',
              )}
            >
              {isInstalling || isRemoving ? <SpinnerIcon className="mr-2 size-3.5 animate-spin" /> : null}
              {selectionMode && isSelected ? <Check size={18} strokeWidth={1.5} absoluteStrokeWidth className="mr-1.5" /> : null}
              {actionLabel}
            </button>
            {provider ? <IntegrationStatusBadge connected={connected} /> : null}
            {provider && isAdmin && !selectionMode ? (
              <button
                type="button"
                className="inline-flex h-10 cursor-pointer items-center gap-1.5 rounded-lg px-2 font-sans text-[12px]/[18px] font-semibold text-gray-500 hover:bg-gray-100 hover:text-black disabled:cursor-not-allowed disabled:opacity-50"
                disabled={isAuthorizing || !configured}
                onClick={handleAuthorizeClick}
              >
                <ExternalLinkIcon className="size-3.5 shrink-0" />
                <span>{!configured ? `${title} unavailable` : connected ? `Manage ${title}` : `Connect ${title}`}</span>
              </button>
            ) : null}
          </div>
        ) : null}
        {blurb ? <p className="font-sans text-[14px] leading-normal whitespace-pre-line text-gray-500">{blurb}</p> : null}
        {integrationHint ? <p className="font-sans text-[13px]/[18px] text-gray-500">{integrationHint}</p> : null}
        {installError ? <p className="font-sans text-[13px]/[18px] text-red-600">{installError}</p> : null}
      </div>
    </div>
  );
});

const WidgetStudio = ({
  initialWidgetId = null,
  initialAgentChatId = null,
  channelId: installChannelId = null,
  onClose,
  // Selection mode (pre-creation Create Group flow): templates are browsed and
  // toggled into a caller-owned selection instead of installed; the Custom
  // section (builder) is hidden because it targets the current workspace.
  selectionMode = false,
  selectedTemplateSlugs = null,
  onToggleTemplate = null,
  initialTemplateSlug = null,
  // Which pane the studio opens on. 'browse' lands on the catalog grid; anything else
  // falls through to the prompt.
  initialView = null,
  // 'modal' is the full library (sidebar + template browsing) inside a ModalPortal.
  // 'composer' is the drawer takeover: builder only, no sidebar, no template fetch,
  // and the host owns the surface chrome.
  variant = 'modal',
}) => {
  const isComposerVariant = variant === 'composer';
  const { unlocked } = useModalContext();
  const {
    workspaceId,
    mainChannelId,
    profileId: currentProfileHashid,
    profile: currentProfile,
    workspace: currentWorkspace,
  } = useRouteWorkspace();
  const channelId = installChannelId || mainChannelId;
  const isAdmin = currentProfile?.role === 'admin';
  // The studio carries the library in its sidebar, so it does fetch templates — but it
  // never *waits* on them (see the composer return branch): the prompt paints first and
  // the sidebar hydrates behind it.
  const canUseWidgetTemplates = Boolean(currentProfile);
  const templateWidgetSignal = useWorkspaceTemplateWidgetSignal(workspaceId);
  const desktopFocusSignal = useDesktopWindowFocusSignal();
  const templateLoadWorkspaceIdRef = useRef(null);
  const templateHasLoadedRef = useRef(false);
  const publishAbortControllerRef = useRef(null);
  const widgetSnapshotCoordinatorRef = useRef(null);
  if (!widgetSnapshotCoordinatorRef.current) widgetSnapshotCoordinatorRef.current = createWidgetSnapshotCoordinator();
  const isResolvingInitialAgentChatRef = useRef(Boolean(initialAgentChatId && !initialWidgetId && workspaceId));
  // The ref gates effects; this renders. Without it a null selection reached by clicking
  // Create new on a chat-targeted open would sit on the resume spinner forever, because
  // initialAgentChatId never stops being set.
  const [isResolvingInitialAgentChat, setIsResolvingInitialAgentChat] = useState(isResolvingInitialAgentChatRef.current);
  const [initialAgentChatResolutionError, setInitialAgentChatResolutionError] = useState(null);
  const [initialAgentChatResolutionAttempt, setInitialAgentChatResolutionAttempt] = useState(0);
  // Nothing is selected unless the caller asked for something: a deep-linked template in
  // the library, a widget or a resumed chat in either, or an explicit opening pane. An
  // untargeted open lands on the empty state, so New neither highlights a sidebar row nor
  // bootstraps a chat before the viewer has said what they want.
  const defaultSelectedItemId = initialView === 'browse' ? BROWSE_WIDGETS_ITEM_ID : initialTemplateSlug;
  const [selectedItemId, setSelectedItemId] = useState(() =>
    initialWidgetId ? customWidgetItemId(initialWidgetId) : initialAgentChatId ? null : defaultSelectedItemId,
  );
  const [expandedSections, setExpandedSections] = useState(() => ({ custom: true, general: true }));
  const [templates, setTemplates] = useState(EMPTY_TEMPLATES);
  const [integrations, setIntegrations] = useState(EMPTY_INTEGRATIONS);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(() => Boolean(canUseWidgetTemplates && workspaceId));
  const [templateError, setTemplateError] = useState(null);
  const [templateErrorSlug, setTemplateErrorSlug] = useState(null);
  const [isLoadingWidgets, setIsLoadingWidgets] = useState(() => Boolean(workspaceId));
  const [widgetSnapshotRevision, setWidgetSnapshotRevision] = useState(0);
  const [widgetsError, setWidgetsError] = useState(null);
  const [builderErrors, setBuilderErrors] = useState({});
  const [isBootstrapping, setIsBootstrapping] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isUnpublishing, setIsUnpublishing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [activeAgentChatId, setActiveAgentChatId] = useState(null);
  const [createAgentChatId, setCreateAgentChatId] = useState(null);
  const [stopTarget, setStopTarget] = useState(null);
  const [isStopping, setIsStopping] = useState(false);
  // The empty state's prompt, handed to the builder's chat to send once it is ready.
  const [seededComposerPrompt, setSeededComposerPrompt] = useState('');
  // Files attached on the empty state before the chat existed — carried along and sent
  // with that same seeded prompt.
  const [seededComposerAttachments, setSeededComposerAttachments] = useState(EMPTY_MEDIA_ATTACHMENTS);
  // The authoring mode picked on the empty state, spent by the chat this create flow mints.
  const [buildNow, setBuildNow] = useState(() => Math.floor(Date.now() / 1000));
  const cancelPublishRequest = useCallback(() => {
    publishAbortControllerRef.current?.abort();
    publishAbortControllerRef.current = null;
    setIsPublishing(false);
  }, []);
  const mutateWidgetSnapshot = useCallback((writeMutation) => {
    const mutation = widgetSnapshotCoordinatorRef.current.mutate(writeMutation);
    setWidgetSnapshotRevision((revision) => revision + 1);
    return mutation;
  }, []);
  const handleRetryInitialAgentChatResolution = useCallback(() => {
    setInitialAgentChatResolutionError(null);
    isResolvingInitialAgentChatRef.current = true;
    setIsResolvingInitialAgentChat(true);
    setInitialAgentChatResolutionAttempt((attempt) => attempt + 1);
  }, []);
  const handleTemplateActionError = useCallback((message, template) => {
    setTemplateError(message);
    setTemplateErrorSlug(message ? template?.slug || null : null);
  }, []);
  const {
    installingSlug,
    removingSlug: removingTemplateSlug,
    installedSlug,
    installTemplate,
    removeTemplate,
    canInstallTemplate,
    canRemoveTemplate,
    resetInstalledSlug,
  } = useWidgetTemplateActions({
    canUseWidgetTemplates,
    workspaceId,
    channelId,
    setTemplates,
    source: 'WidgetStudio',
    onError: handleTemplateActionError,
  });

  const selectedCustomWidgetId = customWidgetIdFromItemId(selectedItemId);
  const selectedWidgetChatId = customWidgetChatIdFromItemId(selectedItemId);
  const isCreateMode = selectedItemId === CREATE_WIDGET_ITEM_ID;
  const isBrowsingWidgets = selectedItemId === BROWSE_WIDGETS_ITEM_ID;
  const isBuilderMode = isCreateMode || Boolean(selectedCustomWidgetId) || Boolean(selectedWidgetChatId);

  const workspaceWidgetsRaw = useLiveQuery(
    async () => {
      if (!workspaceId) return EMPTY_CUSTOM_WIDGETS;

      return db.widgets.where('workspace_id').equals(workspaceId).toArray();
    },
    [workspaceId],
    DEXIE_IS_LOADING,
  );
  const workspaceWidgets = workspaceWidgetsRaw === DEXIE_IS_LOADING ? EMPTY_CUSTOM_WIDGETS : workspaceWidgetsRaw || EMPTY_CUSTOM_WIDGETS;
  const workspaceCustomWidgets = useMemo(() => workspaceWidgets.filter(isCustomWidgetListItem), [workspaceWidgets]);

  const widgetChatsRaw = useLiveQuery(
    async () => {
      if (!workspaceId) return EMPTY_WIDGET_CHATS;

      return studioSidebarWidgetChats(await workspaceWidgetChats(workspaceId));
    },
    [workspaceId],
    DEXIE_IS_LOADING,
  );
  const widgetChats = widgetChatsRaw === DEXIE_IS_LOADING ? EMPTY_WIDGET_CHATS : widgetChatsRaw || EMPTY_WIDGET_CHATS;
  const customWidgets = useMemo(() => {
    const listedWidgetChatIds = new Set(widgetChats.map((chat) => String(chat.id)));
    return workspaceCustomWidgets.filter(
      (widget) =>
        isOwnedCustomWidgetListItem(widget, currentProfileHashid) ||
        (widget.agent_chat_id && listedWidgetChatIds.has(String(widget.agent_chat_id))),
    );
  }, [currentProfileHashid, widgetChats, workspaceCustomWidgets]);
  const selectedWorkspacePublishedWidgetItemId = useMemo(
    () => workspacePublishedWidgetItemIdForSelection(workspaceWidgets, selectedItemId),
    [selectedItemId, workspaceWidgets],
  );
  const selectedWidgetChatWidget = selectedWidgetChatId
    ? customWidgets.find((widget) => widget.agent_chat_id && String(widget.agent_chat_id) === String(selectedWidgetChatId)) || null
    : null;
  const builderWidgetId = selectedCustomWidgetId || selectedWidgetChatWidget?.id || null;

  // Keyed to the selected chat rather than read off widgetChats or activeAgentChatId: both
  // lag a chat that was just written, and a stale "missing" there reads as "deleted".
  const selectedWidgetChatQueryKey = selectedWidgetChatId && workspaceId ? `${workspaceId}:${selectedWidgetChatId}` : null;
  const { loaded: selectedWidgetChatLoaded, value: selectedWidgetChat } = useKeyedLiveQuery(selectedWidgetChatQueryKey, () =>
    db.agent_chats.get([workspaceId, selectedWidgetChatId]),
  );

  const trackedAgentChatQueryKey = activeAgentChatId && workspaceId ? `${workspaceId}:${activeAgentChatId}` : null;
  const { loaded: trackedAgentChatLoaded, value: trackedAgentChat } = useKeyedLiveQuery(trackedAgentChatQueryKey, () =>
    db.agent_chats.get([workspaceId, activeAgentChatId]),
  );
  const trackedWidgetQueryKey =
    !isBuilderMode || !workspaceId
      ? null
      : builderWidgetId
        ? `${workspaceId}:widget:${builderWidgetId}`
        : activeAgentChatId
          ? `${workspaceId}:chat:${activeAgentChatId}`
          : `${workspaceId}:none`;
  const { loaded: trackedWidgetLoaded, value: trackedWidget } = useKeyedLiveQuery(trackedWidgetQueryKey, async () => {
    if (builderWidgetId) return db.widgets.get([workspaceId, builderWidgetId]);
    if (activeAgentChatId) return db.widgets.where('[workspace_id+agent_chat_id]').equals([workspaceId, activeAgentChatId]).first();
    return null;
  });
  const generationTurn = trackedAgentChat?.generation_turn || null;
  const builderState = generationTurn?.builder_state || null;
  const hasGenerationCandidate = builderState?.state === 'ready_to_publish';
  const isGeneratedWidgetPublishing = builderState?.state === 'publishing';
  // Holds the last ready candidate on screen through the next build — a revision
  // replaces the whole turn before its own preview exists, and swapping the iframe to
  // the stale stable runtime mid-build (then back to the new candidate) is the flicker
  // the hold removes. Keyed by chat so a sidebar switch never carries one chat's
  // candidate into another's column.
  const heldGenerationPreviewRef = useRef(null);
  const generationPreview = widgetStudioGenerationPreview(
    generationTurn,
    heldGenerationPreviewRef.current?.chatId === String(activeAgentChatId) ? heldGenerationPreviewRef.current.preview : null,
  );
  useEffect(
    function rememberHeldGenerationPreview() {
      heldGenerationPreviewRef.current =
        generationPreview && activeAgentChatId ? { chatId: String(activeAgentChatId), preview: generationPreview } : null;
    },
    [activeAgentChatId, generationPreview],
  );
  const previewUrl = generationPreview ? generationPreview.url : trackedWidget?.preview_url;
  const previewHeight = (generationPreview ? generationPreview.height : trackedWidget?.preview_height) || 320;
  const templateRuntimeReady =
    Boolean(generationPreview) || !trackedWidget?.template_runtime_state || trackedWidget.template_runtime_state === 'published';
  const hasPreview = Boolean(previewUrl && previewHeight && templateRuntimeReady);
  const title = sentenceCase(generationTurn?.name) || widgetDisplayName(trackedWidget, trackedAgentChat?.title || 'New widget');
  const buildStartedAt = builderState?.active ? builderState.started_at : null;
  const isBuildInProgress = builderState?.active === true;
  const buildElapsedSeconds = buildStartedAt ? Math.max(0, buildNow - buildStartedAt) : null;
  const buildStatusLabel = builderState?.label;
  const isOwnedByCurrentProfile = Boolean(
    trackedWidget?.profile_id && currentProfileHashid && trackedWidget.profile_id === currentProfileHashid,
  );
  const creatorProfile = useLiveQuery(
    () => (workspaceId && trackedWidget?.profile_id ? db.profiles.get([workspaceId, trackedWidget.profile_id]) : null),
    [trackedWidget?.profile_id, workspaceId],
  );
  const canManagePublication = Boolean((trackedWidget || hasGenerationCandidate) && (isOwnedByCurrentProfile || isAdmin));
  // Switching how a chat is authored is the owner's call, which the server enforces;
  // a collaborator sees the mode as a label. A chat with no widget yet is the
  // current user's own.
  const canManageRemoval = Boolean(trackedWidget && (isOwnedByCurrentProfile || (isAdmin && isInstalledTemplateWidget(trackedWidget))));
  const isLegacyPinned = trackedWidget?.template_legacy_pinned === true;
  const isPublished = trackedWidget?.status === 'published';
  const isRunning = trackedAgentChat?.status === 'running';
  // The target carries its own chat id, so a Dexie query still returning the previously
  // tracked chat can never have its Stop aimed at the newly active chat's turn — and
  // requiring the active chat keeps a target reported by the chat we just left from
  // enabling Stop over the one now on screen.
  const canStop = String(stopTarget?.agentChatId) === String(activeAgentChatId) && canStopAgentChat(trackedAgentChat, stopTarget);
  const publicationIsBusy = isPublishing || isUnpublishing || isBuildInProgress;
  const publicationAction = widgetPublicationAction({ widget: trackedWidget, run: generationTurn });
  const removalAction = widgetRemovalAction(trackedWidget);
  const canPublish = Boolean(publicationAction && canManagePublication && !isRunning && !publicationIsBusy && !isDeleting);
  const canUnpublish =
    removalAction === WIDGET_REMOVAL_ACTION.UNPUBLISH && canManagePublication && !isRunning && !publicationIsBusy && !isDeleting;
  const canDelete = removalAction === WIDGET_REMOVAL_ACTION.DELETE && canManageRemoval && !isDeleting && !publicationIsBusy && !isRunning;
  const visibleBuilderErrors =
    builderErrors.general || builderState?.state !== 'failed'
      ? builderErrors
      : { general: builderState.error?.message || 'Widget generation failed.' };
  const isLoadingBuilderWidget = Boolean(builderWidgetId && !trackedWidgetLoaded);
  const latestVersion = Math.max(trackedWidget?.highest_version || 0, readyWidgetPreviewVersion(generationTurn) || 0) || null;
  const versionLabel = widgetVersionLabel({
    latestVersion,
    liveVersion: isPublished ? trackedWidget?.published_version || null : null,
  });

  const handleStop = useCallback(async () => {
    if (isStopping || !canStop) return;

    setIsStopping(true);
    setBuilderErrors({});
    try {
      await stopAgentChat({ agentChat: trackedAgentChat, stopTarget, workspaceId });
    } catch (error) {
      logError('WidgetStudio', 'Failed to stop widget generation', error);
      setBuilderErrors({ general: 'Unable to stop widget generation. Please try again.' });
    } finally {
      setIsStopping(false);
    }
  }, [canStop, isStopping, stopTarget, trackedAgentChat, workspaceId]);

  useEffect(
    function moveStartedCreateChatIntoSidebar() {
      if (!isCreateMode || !createAgentChatId || !isStudioSidebarWidgetChat(trackedAgentChat)) return;

      setSelectedItemId(customWidgetChatItemId(createAgentChatId));
      setCreateAgentChatId(null);
    },
    [createAgentChatId, isCreateMode, trackedAgentChat],
  );

  useEffect(
    function syncSelectedItemWithInitialWidget() {
      if (initialWidgetId) {
        setSelectedItemId(customWidgetItemId(initialWidgetId));
      } else if (!initialAgentChatId) {
        setSelectedItemId(defaultSelectedItemId);
      }
      setTemplateError(null);
      setTemplateErrorSlug(null);
      resetInstalledSlug();
      setBuilderErrors({});
      cancelPublishRequest();
      if (initialWidgetId || !initialAgentChatId) setCreateAgentChatId(null);
    },
    [cancelPublishRequest, defaultSelectedItemId, initialAgentChatId, initialWidgetId, resetInstalledSlug],
  );

  useEffect(
    function resolveInitialAgentChat() {
      if (!initialAgentChatId || initialWidgetId || !workspaceId) {
        isResolvingInitialAgentChatRef.current = false;
        setIsResolvingInitialAgentChat(false);
        setInitialAgentChatResolutionError(null);
        return undefined;
      }

      let cancelled = false;
      isResolvingInitialAgentChatRef.current = true;
      setIsResolvingInitialAgentChat(true);
      setInitialAgentChatResolutionError(null);

      async function resolveSidebarItem() {
        let widget = null;
        // The composer resumes only chats this client has actually seen: a persisted
        // pointer to a purged chat would strand the surface on "Conversation not
        // found" with no sidebar to escape through, and every reopen would repeat it.
        // The library modal keeps its existing deep-link behavior.
        let resumableChat = !isComposerVariant;

        try {
          widget = await db.widgets.where('[workspace_id+agent_chat_id]').equals([workspaceId, initialAgentChatId]).first();
          if (!widget && isComposerVariant) {
            resumableChat = Boolean(await db.agent_chats.get([workspaceId, initialAgentChatId]));
            // A chat clicked seconds after it was created may not have journal-synced
            // yet — ask the server before deciding the pointer is dead, or the click
            // lands on an empty builder instead of the chat it named.
            if (!resumableChat) {
              resumableChat = Boolean(await fetchAgentChatWithGenerationTurn(initialAgentChatId));
            }
          }
        } catch (error) {
          logError('WidgetStudio', 'Failed to resolve initial widget chat', {
            error,
            workspaceId,
            agentChatId: initialAgentChatId,
          });
          if (!cancelled) {
            setInitialAgentChatResolutionError('Unable to open widget builder. Please try again.');
            isResolvingInitialAgentChatRef.current = false;
            setIsResolvingInitialAgentChat(false);
          }
          return;
        }

        if (cancelled) return;

        if (widget) {
          setCreateAgentChatId(null);
          setSelectedItemId(customWidgetItemId(widget.id));
        } else {
          // A null id in create mode just bootstraps a fresh chat, whose id then
          // overwrites the stale resume pointer.
          setCreateAgentChatId(resumableChat ? initialAgentChatId : null);
          setSelectedItemId(CREATE_WIDGET_ITEM_ID);
        }
        isResolvingInitialAgentChatRef.current = false;
        setIsResolvingInitialAgentChat(false);
      }

      resolveSidebarItem();

      return () => {
        cancelled = true;
        isResolvingInitialAgentChatRef.current = false;
      };
    },
    [initialAgentChatId, initialAgentChatResolutionAttempt, initialWidgetId, isComposerVariant, workspaceId],
  );

  useEffect(
    function loadSavedCustomWidgets() {
      if (!workspaceId) {
        setIsLoadingWidgets(false);
        return undefined;
      }

      const abortController = new AbortController();

      setIsLoadingWidgets(true);
      setWidgetsError(null);

      async function fetchWidgets() {
        try {
          const requestRevision = widgetSnapshotCoordinatorRef.current.startRequest();
          const staleStudioWidgetCandidateKeys =
            currentWorkspace?.kind !== 'direct_message'
              ? new Set(
                  (await db.widgets.where('workspace_id').equals(workspaceId).toArray()).filter(isStudioWidgetListItem).map(widgetCacheKey),
                )
              : new Set();
          const response = await apiGet('/widgets', { signal: abortController.signal });
          const data = await response.json();

          if (abortController.signal.aborted) return;

          if (!response.ok || !data.ok || !Array.isArray(data.data?.widgets)) {
            throw new Error(responseErrorMessage(data, 'Could not load saved widgets.'));
          }

          const widgets = data.data.widgets;
          await widgetSnapshotCoordinatorRef.current.commit(requestRevision, () =>
            db.transaction('rw', db.widgets, async () => {
              // Server data stays authoritative. An older request is rejected before
              // this transaction starts, while a local write queued after it wins.
              await db.widgets.bulkPut(widgets);

              if (currentWorkspace?.kind !== 'direct_message') {
                const studioWidgetKeys = new Set(
                  widgets
                    .filter((widget) => String(widget.workspace_id) === String(workspaceId))
                    .filter(isStudioWidgetListItem)
                    .map(widgetCacheKey),
                );
                const existingWidgets = await db.widgets.where('workspace_id').equals(workspaceId).toArray();
                const staleStudioWidgetKeys = existingWidgets
                  .filter(isStudioWidgetListItem)
                  .filter((widget) => staleStudioWidgetCandidateKeys.has(widgetCacheKey(widget)))
                  .filter((widget) => !studioWidgetKeys.has(widgetCacheKey(widget)))
                  .map((widget) => [widget.workspace_id, widget.id]);

                if (staleStudioWidgetKeys.length > 0) await db.widgets.bulkDelete(staleStudioWidgetKeys);
              }
            }),
          );
        } catch (fetchError) {
          if (fetchError?.name === 'AbortError') return;

          logError('WidgetStudio', 'Failed to load saved widgets', fetchError, {
            workspaceId,
            workspaceKind: currentWorkspace?.kind,
          });
          setWidgetsError(fetchError.message || 'Could not load saved widgets.');
        } finally {
          if (!abortController.signal.aborted) setIsLoadingWidgets(false);
        }
      }

      fetchWidgets();

      return () => abortController.abort();
    },
    [currentWorkspace?.kind, widgetSnapshotRevision, workspaceId],
  );

  useEffect(
    function loadWidgetTemplates() {
      if (!canUseWidgetTemplates || !workspaceId) {
        templateLoadWorkspaceIdRef.current = null;
        templateHasLoadedRef.current = false;
        setTemplates(EMPTY_TEMPLATES);
        setIntegrations(EMPTY_INTEGRATIONS);
        setIsLoadingTemplates(false);
        setTemplateError(null);
        setTemplateErrorSlug(null);
        return undefined;
      }

      const abortController = new AbortController();
      const query = new URLSearchParams({ workspace_id: workspaceId });
      const isWorkspaceLoad = templateLoadWorkspaceIdRef.current !== workspaceId;

      if (isWorkspaceLoad) templateHasLoadedRef.current = false;

      const isBackgroundRefresh = !isWorkspaceLoad && templateHasLoadedRef.current;

      templateLoadWorkspaceIdRef.current = workspaceId;

      if (!isBackgroundRefresh) {
        setIsLoadingTemplates(true);
        setTemplateError(null);
        setTemplateErrorSlug(null);
      }

      async function fetchTemplates() {
        try {
          const response = await apiGet(`/widget_templates?${query.toString()}`, { signal: abortController.signal });
          const data = await response.json();

          if (abortController.signal.aborted) return;

          if (!response.ok || !data.ok || !Array.isArray(data.data?.widget_templates)) {
            throw new Error(responseErrorMessage(data, 'Could not load widget templates.'));
          }

          templateHasLoadedRef.current = true;
          // Staff can pull a template from the studio grid by unchecking Live on its listing.
          // The API still serves it so the gallery, Create group chat, and iOS are unaffected.
          setTemplates(data.data.widget_templates.filter((template) => template.live !== false));
          setIntegrations(data.data.integrations || EMPTY_INTEGRATIONS);
        } catch (fetchError) {
          if (fetchError?.name === 'AbortError') return;

          logError('WidgetStudio', 'Failed to load widget templates', { error: fetchError });
          if (!isBackgroundRefresh) {
            templateHasLoadedRef.current = false;
            setTemplateError(fetchError.message || 'Could not load widget templates.');
            setTemplateErrorSlug(null);
            setTemplates(EMPTY_TEMPLATES);
            setIntegrations(EMPTY_INTEGRATIONS);
          }
        } finally {
          if (!abortController.signal.aborted && !isBackgroundRefresh) setIsLoadingTemplates(false);
        }
      }

      fetchTemplates();

      return () => abortController.abort();
    },
    [canUseWidgetTemplates, desktopFocusSignal, templateWidgetSignal, workspaceId],
  );

  useEffect(
    function ensureValidSidebarSelection() {
      if (isResolvingInitialAgentChatRef.current || isLoadingTemplates || isLoadingWidgets) return;
      if (selectedItemId === CREATE_WIDGET_ITEM_ID || selectedItemId === BROWSE_WIDGETS_ITEM_ID) return;

      if (selectedWorkspacePublishedWidgetItemId) {
        if (selectedItemId !== selectedWorkspacePublishedWidgetItemId) setSelectedItemId(selectedWorkspacePublishedWidgetItemId);
        return;
      }

      if (selectedCustomWidgetId) {
        if (!trackedWidgetLoaded) return;
        const listedWidget = customWidgets.find((widget) => String(widget.id) === String(selectedCustomWidgetId));
        if (listedWidget?.agent_chat_id) {
          setSelectedItemId(customWidgetChatItemId(listedWidget.agent_chat_id));
          return;
        }
        const selectedCustomWidgetStillExists = Boolean(trackedWidget || listedWidget);
        if (selectedCustomWidgetStillExists) return;
      }

      if (selectedWidgetChatId) {
        if (!selectedWidgetChatLoaded) return;
        // Mirrors what the sidebar actually renders: the widget row, or the chat on its own.
        if (selectedWidgetChatWidget || isStudioSidebarWidgetChat(selectedWidgetChat)) return;
      }

      // Nothing valid selected, so land on nothing: the surface opens blank rather than
      // grabbing the first template. Defaulting to one flashed its black sidebar row for
      // a frame on the way to a resumed chat, and made the library open on a widget the
      // viewer never asked for.
      const selectedTemplateStillExists = Boolean(selectedItemId && templates.some((template) => template.slug === selectedItemId));

      if (!selectedTemplateStillExists && selectedItemId !== null) {
        setSelectedItemId(null);
      }
    },
    [
      customWidgets,
      isLoadingTemplates,
      isLoadingWidgets,
      selectedCustomWidgetId,
      selectedItemId,
      selectedWorkspacePublishedWidgetItemId,
      selectedWidgetChat,
      selectedWidgetChatId,
      selectedWidgetChatLoaded,
      selectedWidgetChatWidget,
      templates,
      trackedWidget,
      trackedWidgetLoaded,
    ],
  );

  useEffect(
    function resetBuilderChatWhenSelectedBuilderChanges() {
      cancelPublishRequest();

      if (selectedWidgetChatId) {
        setActiveAgentChatId(selectedWidgetChatId);
        setBuilderErrors({});
        setIsBootstrapping(false);
        return;
      }

      if (isCreateMode && createAgentChatId) {
        setActiveAgentChatId(createAgentChatId);
        setBuilderErrors({});
        setIsBootstrapping(false);
        return;
      }

      if (selectedCustomWidgetId && trackedWidget?.agent_chat_id) {
        setActiveAgentChatId(trackedWidget.agent_chat_id);
        setBuilderErrors({});
        setIsBootstrapping(false);
        return;
      }

      setActiveAgentChatId(null);
      setBuilderErrors({});
      setIsBootstrapping(false);
    },
    [cancelPublishRequest, createAgentChatId, isCreateMode, selectedCustomWidgetId, selectedWidgetChatId, trackedWidget?.agent_chat_id],
  );

  const missingWidgetChatId =
    selectedCustomWidgetId && trackedWidget?.agent_chat_id && trackedAgentChatLoaded && !trackedAgentChat
      ? trackedWidget.agent_chat_id
      : selectedWidgetChatId && selectedWidgetChatLoaded && !selectedWidgetChat
        ? selectedWidgetChatId
        : null;
  const missingWidgetChatWidgetId = selectedCustomWidgetId || selectedWidgetChatWidget?.id || null;
  const missingWidgetChatIsRepairable =
    isTrackedWidgetReadyForMissingChatRepair(trackedWidgetLoaded, selectedWidgetChatId, activeAgentChatId) &&
    canHealMissingWidgetChat(trackedWidget, currentProfileHashid, missingWidgetChatWidgetId);

  // A local miss is not proof of deletion: journal hydration can lag behind a click.
  // Confirm an eligible chat is absent on the server, then heal either selection shape
  // through the same path so the widget can bootstrap a replacement conversation.
  // Ineligible template chats also return 404 to collaborators, but their links are live.
  useEffect(() => {
    if (!workspaceId || !missingWidgetChatId) return undefined;

    let cancelled = false;

    const healMissingWidgetChat = async () => {
      try {
        const serverChat = await fetchAgentChatWithGenerationTurn(missingWidgetChatId);
        if (cancelled || serverChat || !missingWidgetChatIsRepairable) return;

        await mutateWidgetSnapshot(() =>
          db.widgets.where('[workspace_id+agent_chat_id]').equals([workspaceId, missingWidgetChatId]).modify({ agent_chat_id: null }),
        );
        if (cancelled) return;

        setSelectedItemId(missingWidgetChatWidgetId ? customWidgetItemId(missingWidgetChatWidgetId) : null);
      } catch (error) {
        logError('WidgetStudio', 'Failed to resolve dangling widget chat link', {
          error,
          workspaceId,
          agentChatId: missingWidgetChatId,
        });
        if (!cancelled) setBuilderErrors({ general: error.message || 'Unable to open widget builder. Please try again.' });
      }
    };

    healMissingWidgetChat();

    return () => {
      cancelled = true;
    };
  }, [missingWidgetChatId, missingWidgetChatIsRepairable, missingWidgetChatWidgetId, mutateWidgetSnapshot, workspaceId]);

  useEffect(function abortPublishRequestOnUnmount() {
    return () => publishAbortControllerRef.current?.abort();
  }, []);

  useEffect(() => {
    if (!isBuildInProgress) return undefined;

    setBuildNow(Math.floor(Date.now() / 1000));
    const intervalId = window.setInterval(() => setBuildNow(Math.floor(Date.now() / 1000)), 1000);

    return () => window.clearInterval(intervalId);
  }, [isBuildInProgress, buildStartedAt]);

  // The after-purge recovery path: a directly selected widget with no chat link left
  // gets a fresh chat minted for it. Chat-based selections never reach this — a live
  // chat needs no bootstrap, and a dead one is healed into a widget selection above.
  useEffect(() => {
    if (!selectedCustomWidgetId || !workspaceId || !trackedWidgetLoaded) return undefined;
    // A row that still names a chat needs no minting: the sidebar reconciliation
    // re-keys the selection onto that chat.
    if (trackedWidget?.agent_chat_id) return undefined;

    let cancelled = false;

    const bootstrapExistingWidgetChat = async () => {
      setBuilderErrors({});
      setIsBootstrapping(true);

      try {
        const response = await apiPost('/agent_chats', {
          workspace_id: workspaceId,
          agent_chat: { widget_id: selectedCustomWidgetId },
        });
        const data = await response.json();

        if (cancelled) return;

        if (response.ok && data.ok && data.data?.agent_chat) {
          const chat = data.data.agent_chat;
          await storeAgentChatWithGenerationTurn(chat);
          await mutateWidgetSnapshot(() => db.widgets.update([workspaceId, selectedCustomWidgetId], { agent_chat_id: chat.id }));
          setActiveAgentChatId(chat.id);
          return;
        }

        setBuilderErrors(data.errors || { general: 'Unable to open widget builder. Please try again.' });
      } catch (error) {
        logError('WidgetStudio', 'Failed to bootstrap widget chat', error);
        if (!cancelled) setBuilderErrors({ general: 'Unable to open widget builder. Please try again.' });
      } finally {
        if (!cancelled) setIsBootstrapping(false);
      }
    };

    bootstrapExistingWidgetChat();

    return () => {
      cancelled = true;
    };
  }, [mutateWidgetSnapshot, workspaceId, selectedCustomWidgetId, trackedWidget?.agent_chat_id, trackedWidgetLoaded]);

  useEffect(() => {
    if (!isCreateMode || builderWidgetId || activeAgentChatId || createAgentChatId || !workspaceId) return undefined;

    let cancelled = false;

    const bootstrapNewWidgetChat = async () => {
      setActiveAgentChatId(null);
      setBuilderErrors({});
      setIsBootstrapping(true);

      try {
        const response = await apiPost('/agent_chats', {
          workspace_id: workspaceId,
          agent_chat: {
            kind: 'widget',
          },
        });
        const data = await response.json();

        if (cancelled) return;

        if (response.ok && data.ok && data.data?.agent_chat) {
          const chat = data.data.agent_chat;
          await storeAgentChatWithGenerationTurn(chat);
          setCreateAgentChatId(chat.id);
          setActiveAgentChatId(chat.id);
          return;
        }

        setBuilderErrors(data.errors || { general: 'Unable to open widget builder. Please try again.' });
      } catch (error) {
        logError('WidgetStudio', 'Failed to bootstrap widget chat', error);
        if (!cancelled) setBuilderErrors({ general: 'Unable to open widget builder. Please try again.' });
      } finally {
        if (!cancelled) setIsBootstrapping(false);
      }
    };

    bootstrapNewWidgetChat();

    return () => {
      cancelled = true;
    };
  }, [activeAgentChatId, builderWidgetId, createAgentChatId, isCreateMode, workspaceId]);

  const pendingTurnsByChatId = usePendingWidgetGenerationTurnsByChatId(workspaceId);

  useEffect(function ensureTurnReconciler() {
    ensureWidgetGenerationTurnReconciler();
  }, []);

  const sidebarSections = useMemo(
    () => widgetSidebarSections(templates, customWidgets, widgetChats, { includeCustom: !selectionMode, pendingTurnsByChatId }),
    [customWidgets, pendingTurnsByChatId, selectionMode, templates, widgetChats],
  );
  const workspaceItems = useMemo(
    () => workspacePublishedWidgetSidebarItems(workspaceWidgets, widgetChats, pendingTurnsByChatId),
    [pendingTurnsByChatId, widgetChats, workspaceWidgets],
  );
  const workspaceItemWidgetIds = useMemo(() => new Set(workspaceItems.map((item) => String(item.widgetId))), [workspaceItems]);
  // What the viewer has actually made. The Create row stays out — the studio's rail gives
  // it a button of its own rather than burying it in the list.
  const recentItems = useMemo(
    () =>
      (sidebarSections.find((section) => section.id === 'custom')?.items || []).filter(
        (item) => item.id !== CREATE_WIDGET_ITEM_ID && !workspaceItemWidgetIds.has(String(item.widgetId)),
      ),
    [sidebarSections, workspaceItemWidgetIds],
  );
  const selectedItem = useMemo(() => selectedWidgetSidebarItem(sidebarSections, selectedItemId), [selectedItemId, sidebarSections]);
  const selectedTemplate = selectedItem?.template || null;
  const selectedTemplateProvider = selectedTemplate?.required_integrations?.[0] || null;
  const selectedTemplateIntegrationStatus = selectedTemplateProvider ? integrations[selectedTemplateProvider] : null;
  const isLoadingModalData = isLoadingTemplates || isLoadingWidgets;
  // templateErrorSlug distinguishes an install failure (scoped to one template) from a load failure (sidebar-wide).
  const templateLoadError = templateErrorSlug ? null : templateError;
  const selectedTemplateInstallError = templateErrorSlug && templateErrorSlug === selectedTemplate?.slug ? templateError : null;

  // Where a template detail's Back should land: the surface the pick was made from.
  // Only the create screen (null) and the browse grid can originate a detail view;
  // any other transition keeps the previous target.
  const [detailBackId, setDetailBackId] = useState(BROWSE_WIDGETS_ITEM_ID);
  // Bumped on every pick that lands on the home page, so the rail's anchors re-scroll
  // even when the selection itself has not changed — clicking Browse widgets while
  // already on the catalog has to take you back to it.
  const [homeScrollNonce, setHomeScrollNonce] = useState(0);
  const handleSelectItem = useCallback(
    function selectWidgetSidebarItem(itemId) {
      cancelPublishRequest();
      if (itemId === null || itemId === BROWSE_WIDGETS_ITEM_ID) setHomeScrollNonce((nonce) => nonce + 1);
      if (selectedItemId === null || selectedItemId === CREATE_WIDGET_ITEM_ID) {
        setDetailBackId(null);
      } else if (selectedItemId === BROWSE_WIDGETS_ITEM_ID) {
        setDetailBackId(BROWSE_WIDGETS_ITEM_ID);
      }
      setSelectedItemId(itemId);
      setTemplateError(null);
      setTemplateErrorSlug(null);
      setInitialAgentChatResolutionError(null);
      // Picking anything by hand drops a pending prompt: coming back to Create Widget
      // later bootstraps a fresh chat, and it should start empty rather than replay the
      // prompt from the last visit. Forgetting the chat the last visit created is what
      // makes the next one fresh.
      setCreateAgentChatId(null);
      setSeededComposerPrompt('');
      setSeededComposerAttachments(EMPTY_MEDIA_ATTACHMENTS);
      resetInstalledSlug();
    },
    [cancelPublishRequest, resetInstalledSlug, selectedItemId],
  );

  const handleDetailBack = useCallback(
    function backFromTemplateDetail() {
      handleSelectItem(detailBackId);
    },
    [detailBackId, handleSelectItem],
  );

  // The empty state already took the prompt (and whatever it attached), so entering the
  // builder should land mid-turn: create mode bootstraps the chat and the chat sends this
  // the moment it is ready. Order matters — handleSelectItem clears the seed, so it has
  // to run before the seed is set.
  const handleStartFromPrompt = useCallback(
    function startBuilderFromPrompt(prompt, attachments) {
      handleSelectItem(CREATE_WIDGET_ITEM_ID);
      setSeededComposerPrompt(prompt || '');
      setSeededComposerAttachments(attachments?.length ? attachments : EMPTY_MEDIA_ATTACHMENTS);
    },
    [handleSelectItem],
  );

  const handleToggleSection = useCallback(function toggleWidgetSidebarSection(sectionId) {
    setExpandedSections((currentSections) => ({ ...currentSections, [sectionId]: !currentSections[sectionId] }));
  }, []);

  const handlePublish = useCallback(async () => {
    if (!canPublish) return;
    if (!unlocked()) return;

    if (publicationAction === WIDGET_PUBLICATION_ACTION.PUBLISH_WIDGET) {
      setIsPublishing(true);
      setBuilderErrors({});

      try {
        const response = await apiPost(`/widgets/${trackedWidget.id}/publish`);
        const data = await response.json();

        if (response.ok && data.ok) {
          if (data.data?.widget) await db.widgets.put(data.data.widget);
          clearPendingWidgetGenerationTurn(workspaceId, activeAgentChatId || trackedWidget.agent_chat_id);
          await clearFingerprint('board_items');
          return;
        }

        setBuilderErrors(data.errors || { general: 'Unable to publish widget. Please try again.' });
      } catch (error) {
        logError('WidgetStudio', 'Failed to publish widget', error);
        setBuilderErrors({ general: 'Unable to publish widget. Please try again.' });
      } finally {
        setIsPublishing(false);
      }
      return;
    }

    const runId = generationTurn.id;
    publishAbortControllerRef.current?.abort();
    const abortController = new AbortController();
    publishAbortControllerRef.current = abortController;
    setIsPublishing(true);
    setBuilderErrors({});

    try {
      const promotion = await promoteWidgetGenerationRun(runId, { signal: abortController.signal });
      if (!promotion) return;

      const { response, data } = promotion;

      if (response.ok && data.ok) {
        if (data.data?.generation_turn || data.data?.working_revision) {
          await applyWidgetGenerationState(data.data, {
            workspaceId,
            agentChatId: activeAgentChatId || trackedWidget?.agent_chat_id,
          });
        }
        if (data.data?.widget) await db.widgets.put(data.data.widget);
        clearPendingWidgetGenerationTurn(workspaceId, activeAgentChatId || trackedWidget?.agent_chat_id);
        return;
      }

      setBuilderErrors({ general: responseErrorMessage(data, 'Unable to publish widget. Please try again.') });
    } catch (error) {
      if (error.name === 'AbortError') return;

      logError('WidgetStudio', 'Failed to publish generated widget', error);
      setBuilderErrors({ general: 'Unable to publish widget. Please try again.' });
    } finally {
      if (publishAbortControllerRef.current === abortController) {
        publishAbortControllerRef.current = null;
        setIsPublishing(false);
      }
    }
  }, [activeAgentChatId, canPublish, generationTurn, publicationAction, trackedWidget, unlocked, workspaceId]);

  const handleUnpublish = useCallback(async () => {
    if (!canUnpublish) return;
    if (!unlocked()) return;

    setIsUnpublishing(true);
    setBuilderErrors({});

    try {
      await removeWidget(trackedWidget, WIDGET_REMOVAL_ACTION.UNPUBLISH);
    } catch (error) {
      logError('WidgetStudio', 'Failed to unpublish widget', error);
      setBuilderErrors({ general: 'Unable to unpublish widget. Please try again.' });
    } finally {
      setIsUnpublishing(false);
    }
  }, [canUnpublish, trackedWidget, unlocked]);

  const handleOpenDeleteConfirm = useCallback(() => setIsDeleteConfirmOpen(true), []);
  const handleCloseDeleteConfirm = useCallback(() => setIsDeleteConfirmOpen(false), []);

  const handleConfirmDelete = useCallback(async () => {
    if (!trackedWidget || !canDelete) return;

    setIsDeleting(true);
    setIsDeleteConfirmOpen(false);
    setBuilderErrors({});

    try {
      await removeWidget(trackedWidget, WIDGET_REMOVAL_ACTION.DELETE);
      // The create-mode chat belongs to the deleted widget now; drop it so a fresh one bootstraps.
      if (createAgentChatId && trackedWidget.agent_chat_id === createAgentChatId) setCreateAgentChatId(null);
      setSelectedItemId(CREATE_WIDGET_ITEM_ID);
    } catch (error) {
      logError('WidgetStudio', 'Failed to delete widget', error);
      setBuilderErrors({ general: 'Unable to delete widget. Please try again.' });
    } finally {
      setIsDeleting(false);
    }
  }, [canDelete, createAgentChatId, trackedWidget]);

  const handleInstallTemplate = useCallback(
    function installSelectedTemplate() {
      if (selectedTemplate) installTemplate(selectedTemplate);
    },
    [installTemplate, selectedTemplate],
  );

  const handleRemoveTemplate = useCallback(
    function removeSelectedTemplate() {
      if (selectedTemplate) removeTemplate(selectedTemplate);
    },
    [removeTemplate, selectedTemplate],
  );

  const canInstallSelectedTemplate = canInstallTemplate(selectedTemplate, { isLoading: isLoadingTemplates });
  const canRemoveSelectedTemplate = canRemoveTemplate(selectedTemplate, { isLoading: isLoadingTemplates });

  const handleToggleSelectedTemplate = useCallback(
    function toggleSelectedTemplate() {
      if (selectedTemplate && onToggleTemplate) onToggleTemplate(selectedTemplate.slug);
    },
    [onToggleTemplate, selectedTemplate],
  );

  const builderPanel = (
    <WidgetBuilderPanel
      activeAgentChatId={activeAgentChatId}
      buildElapsedSeconds={buildElapsedSeconds}
      canDelete={canDelete}
      canManagePublication={canManagePublication}
      canManageRemoval={canManageRemoval}
      canPublish={canPublish}
      canStop={canStop}
      canUnpublish={canUnpublish}
      composerAutoSubmit={seededComposerPrompt}
      composerAutoSubmitAttachments={seededComposerAttachments}
      creatorProfile={isUserCreatedWidget(trackedWidget) ? creatorProfile : null}
      errors={visibleBuilderErrors}
      fillsSurface={isComposerVariant}
      headless={isComposerVariant}
      handleOpenDeleteConfirm={handleOpenDeleteConfirm}
      handlePublish={handlePublish}
      handleUnpublish={handleUnpublish}
      hasPreview={hasPreview}
      isBootstrapping={isBootstrapping}
      isBuildInProgress={isBuildInProgress}
      isDeleting={isDeleting}
      isLoadingBuilderWidget={isLoadingBuilderWidget}
      isPublishing={isPublishing || isGeneratedWidgetPublishing}
      isRunning={isRunning}
      isStopping={isStopping}
      isUnpublishing={isUnpublishing}
      onClose={isComposerVariant ? onClose : undefined}
      onStopTargetChange={setStopTarget}
      onStop={handleStop}
      previewHeight={previewHeight}
      previewUrl={previewUrl}
      removalAction={removalAction}
      buildStatusLabel={buildStatusLabel}
      title={title}
      trackedWidget={trackedWidget}
      versionLabel={versionLabel}
      isLegacyPinned={isLegacyPinned}
    />
  );

  const deleteConfirmation = (
    <ModalPortal open={isDeleteConfirmOpen} onClose={handleCloseDeleteConfirm} size="mini" keychainId={KEYCHAIN.MODAL__REMOVE_WIDGET}>
      <RemoveWidgetConfirmation onClose={handleCloseDeleteConfirm} onConfirm={handleConfirmDelete} />
    </ModalPortal>
  );

  const templateDetailPanel = (
    <WidgetTemplateDetailPanel
      item={selectedItem}
      template={selectedTemplate}
      integrationStatus={selectedTemplateIntegrationStatus}
      isAdmin={isAdmin}
      isInstalling={installingSlug === selectedTemplate?.slug}
      isRemoving={removingTemplateSlug === selectedTemplate?.slug}
      wasInstalled={installedSlug === selectedTemplate?.slug}
      installError={selectedTemplateInstallError}
      canInstall={canInstallSelectedTemplate}
      canRemove={canRemoveSelectedTemplate}
      workspaceId={workspaceId}
      onInstall={handleInstallTemplate}
      onRemove={handleRemoveTemplate}
      onError={handleTemplateActionError}
      desktopFocusSignal={desktopFocusSignal}
      selectionMode={selectionMode}
      isSelected={Boolean(selectedTemplate && selectedTemplateSlugs?.has(selectedTemplate.slug))}
      onToggleSelected={handleToggleSelectedTemplate}
      headless={isComposerVariant}
      onClose={isComposerVariant ? onClose : null}
    />
  );

  // The studio host already supplies the surface, so it renders chrome-free. It
  // deliberately does NOT gate on isLoadingModalData the way the modal does: the surface
  // paints immediately and the sidebar fills in late. A null selection means one of two
  // things — a resumed draft chat still resolving, which gets the spinner rather than the
  // panel's "Widget chat unavailable" fallback, or an untargeted open, which gets the
  if (isComposerVariant) {
    const isResumingChat = isResolvingInitialAgentChat;
    // Create new and Browse widgets are the same page, so both land on the home surface —
    // the selection only decides which section it opens scrolled to.
    const composerPanel = isBuilderMode ? (
      builderPanel
    ) : selectedItem && !isBrowsingWidgets ? (
      templateDetailPanel
    ) : initialAgentChatResolutionError ? (
      <WidgetModalErrorState message={initialAgentChatResolutionError} onRetry={handleRetryInitialAgentChatResolution} />
    ) : isResumingChat ? (
      <WidgetModalLoadingState />
    ) : (
      <WidgetComposerHome
        onStart={handleStartFromPrompt}
        onSelectItem={handleSelectItem}
        templates={templates}
        loadingTemplates={isLoadingTemplates}
        scrollTarget={isBrowsingWidgets ? 'grid' : 'prompt'}
        scrollNonce={homeScrollNonce}
        workspaceId={workspaceId}
      />
    );
    // The header names whichever pane is showing, so the studio reads as one surface
    // being navigated rather than a rail glued to a stack of unrelated pages. The home
    // page goes untitled in both of its positions — its own headings do the naming.
    const studioTitle = isBuilderMode
      ? title
      : selectedItem && !isBrowsingWidgets
        ? templateDisplayName(selectedTemplate, selectedItem.label)
        : '';

    return (
      <>
        {/* Transparent, not white: the host panel owns the surface colour now, and the
            rail is the only thing that paints over it. */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden text-black">
          <WidgetStudioHeader
            title={studioTitle}
            creatorProfile={isBuilderMode && isUserCreatedWidget(trackedWidget) ? creatorProfile : null}
            onBack={selectedItem && !isBuilderMode ? handleDetailBack : null}
            onClose={onClose}
          />
          <div className="flex min-h-0 flex-1 overflow-hidden">
            <WidgetComposerSidebar
              className="max-sm:hidden"
              workspaceTitle={currentWorkspace?.title || ''}
              workspaceItems={workspaceItems}
              recentItems={recentItems}
              selectedItemId={selectedItemId}
              error={widgetsError || templateLoadError}
              loading={isLoadingModalData}
              onSelectItem={handleSelectItem}
            />
            {/* The content pane as a large floating card on the gray-200 shell — the
                widgets board's card-on-surface treatment, with the gutter and rounded
                corners on every side now that the backdrop dims everything behind the
                panel. The gutter is also what keeps the gray-200 stages inside from
                reading as part of the rail. */}
            <div className="mx-3 mb-3 flex min-h-0 min-w-0 flex-1 overflow-hidden rounded-xl border-[0.5px] border-white bg-gray-50 shadow-[0_2px_8px_rgba(0,0,0,0.08)]">
              {composerPanel}
            </div>
          </div>
        </div>
        {deleteConfirmation}
      </>
    );
  }

  return (
    <>
      <Modal
        onClose={onClose}
        showClose={false}
        padding="none"
        className="rounded-2xl border-0 bg-white text-black shadow-[0_9px_40px_rgba(0,0,0,0.2)] dark:bg-white dark:text-black"
      >
        <ModalBody padding="none" className="min-h-0 overflow-hidden bg-white text-black dark:bg-white dark:text-black">
          <div className="flex min-h-0 flex-1 flex-col">
            <WidgetModalHeader onClose={onClose} />

            <ModalContent className="flex min-h-0 overflow-hidden bg-white dark:bg-white">
              {isLoadingModalData ? (
                <WidgetModalLoadingState />
              ) : (
                <>
                  <WidgetModalSidebar
                    sections={sidebarSections}
                    selectedItemId={selectedItemId}
                    expandedSections={expandedSections}
                    error={widgetsError || templateLoadError}
                    onToggleSection={handleToggleSection}
                    onSelectItem={handleSelectItem}
                  />
                  {initialAgentChatResolutionError ? (
                    <WidgetModalErrorState message={initialAgentChatResolutionError} onRetry={handleRetryInitialAgentChatResolution} />
                  ) : isBuilderMode ? (
                    builderPanel
                  ) : (
                    templateDetailPanel
                  )}
                </>
              )}
            </ModalContent>
          </div>
        </ModalBody>
      </Modal>

      {deleteConfirmation}
    </>
  );
};

export default WidgetStudio;
